import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { buildManualConnectionPayload, hasManualProviderChanges, hasUsableNewVerifyToken } from "./manualConnection.mjs";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	TextField,
	Box,
	Menu,
	MenuItem,
	Tab,
	Tabs,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Slider,
	Switch,
	ListItemIcon,
	ListItemText,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	BugReport,
	Refresh,
	CheckCircleOutline,
	ErrorOutline,
	WhatsApp,
	Public,
	Facebook,
	FileCopy,
	MoreVert,
	PauseCircleOutline,
	PowerSettingsNew,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import {
	getConnectionChannel,
	isQrCodeConnectionNeedingAttention,
	normalizeConnectionStatus,
} from "../../utils/connections";

const SCRIPT_COLOR_PRESETS = [
	{ value: "flowch", label: "Flowch", hex: "#0c8c63" },
	{ value: "blue", label: "Azul", hex: "#2563eb" },
	{ value: "orange", label: "Laranja", hex: "#ea580c" },
	{ value: "pink", label: "Rosa", hex: "#db2777" },
	{ value: "slate", label: "Neutro", hex: "#475569" },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const TEMPLATE_VARIABLE_PRESETS = ["nome", "empresa", "data", "horario", "link"];
const TEMPLATE_VARIABLE_EXAMPLES = {
	nome: "Maria",
	empresa: "Clínica Exemplo",
	data: "20/09/2026",
	horario: "14:30",
	link: "https://exemplo.com/agendamento",
};

const extractTemplateVariables = value => {
	const tokens = [];
	const pattern = /\{\{\s*([^}]+?)\s*\}\}/g;
	let match;
	while ((match = pattern.exec(String(value || ""))) !== null) {
		const token = String(match[1] || "").trim();
		if (token && !tokens.includes(token)) tokens.push(token);
	}
	return tokens;
};

const createEmptyTemplateForm = () => ({
	name: "",
	friendlyName: "",
	language: "pt_BR",
	category: "UTILITY",
	bodyText: "",
	parameterFormat: "NAMED",
	parameterExamples: { ...TEMPLATE_VARIABLE_EXAMPLES },
	positionalExamples: [],
});

const createEmptyMetaApplicationForm = () => ({
	name: "",
	appId: "",
	appSecret: "",
	verifyToken: "",
	embeddedSignupConfigId: "",
	coexistenceConfigId: "",
	appMode: "live",
});

const normalizeHexColor = (value, fallback = "#0c8c63") => {
	const cleaned = String(value || "").trim().replace(/^#/, "");
	if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(cleaned)) {
		return fallback;
	}

	if (cleaned.length === 3) {
		return `#${cleaned
			.split("")
			.map(char => `${char}${char}`)
			.join("")
			.toLowerCase()}`;
	}

	return `#${cleaned.toLowerCase()}`;
};

const hexToRgb = hex => {
	const normalized = normalizeHexColor(hex).replace("#", "");
	return {
		r: parseInt(normalized.slice(0, 2), 16),
		g: parseInt(normalized.slice(2, 4), 16),
		b: parseInt(normalized.slice(4, 6), 16),
	};
};

const rgbToHex = (r, g, b) => {
	const toHex = value => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsl = ({ r, g, b }) => {
	const red = r / 255;
	const greenColor = g / 255;
	const blue = b / 255;
	const max = Math.max(red, greenColor, blue);
	const min = Math.min(red, greenColor, blue);
	const delta = max - min;

	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (delta !== 0) {
		s = delta / (1 - Math.abs(2 * l - 1));
		switch (max) {
			case red:
				h = 60 * (((greenColor - blue) / delta) % 6);
				break;
			case greenColor:
				h = 60 * ((blue - red) / delta + 2);
				break;
			default:
				h = 60 * ((red - greenColor) / delta + 4);
		}
	}

	if (h < 0) h += 360;

	return {
		h,
		s: s * 100,
		l: l * 100,
	};
};

const hslToRgb = ({ h, s, l }) => {
	const hue = ((h % 360) + 360) % 360;
	const saturation = clamp(s, 0, 100) / 100;
	const lightness = clamp(l, 0, 100) / 100;

	const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
	const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
	const m = lightness - c / 2;

	let red = 0;
	let greenColor = 0;
	let blue = 0;

	if (hue < 60) {
		red = c;
		greenColor = x;
	} else if (hue < 120) {
		red = x;
		greenColor = c;
	} else if (hue < 180) {
		greenColor = c;
		blue = x;
	} else if (hue < 240) {
		greenColor = x;
		blue = c;
	} else if (hue < 300) {
		red = x;
		blue = c;
	} else {
		red = c;
		blue = x;
	}

	return {
		r: (red + m) * 255,
		g: (greenColor + m) * 255,
		b: (blue + m) * 255,
	};
};

const shiftHexHue = (hex, offset) => {
	const hsl = rgbToHsl(hexToRgb(hex));
	const shifted = {
		h: hsl.h + Number(offset || 0),
		s: hsl.s,
		l: hsl.l,
	};
	const rgb = hslToRgb(shifted);
	return rgbToHex(rgb.r, rgb.g, rgb.b);
};

const adjustHexLightness = (hex, delta) => {
	const hsl = rgbToHsl(hexToRgb(hex));
	const adjusted = {
		h: hsl.h,
		s: hsl.s,
		l: clamp(hsl.l + Number(delta || 0), 0, 100),
	};
	const rgb = hslToRgb(adjusted);
	return rgbToHex(rgb.r, rgb.g, rgb.b);
};

const useStyles = makeStyles(theme => {
	const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

	return ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		paddingTop: 10,
		overflow: "auto",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "18px 0 0 0",
		border: "none",
		boxShadow: "none",
		...theme.scrollbarStyles,
		[theme.breakpoints.down("sm")]: {
			borderRadius: 0,
		},
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
	},
	tableWrapper: {
		width: "100%",
		overflowX: "auto",
		WebkitOverflowScrolling: "touch",
		"& .MuiTableCell-root": {
			padding: "6px 14px",
		},
	},
	tableMinWidth: {
		tableLayout: "auto",
		width: "max-content",
		minWidth: "100%",
	},
	headerTextCell: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: "none",
	},
	bodyTextCell: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: 200,
	},
	nameCellContent: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
	},
	nameTypeIcon: {
		fontSize: "1rem",
		opacity: 0.9,
	},
	colId: {
		width: 72,
		maxWidth: 72,
	},
	colName: {
		width: "auto",
	},
	colType: {
		width: "auto",
	},
	colStatus: {
		width: "auto",
	},
	colSession: {
		width: "auto",
	},
	colUpdated: {
		width: "auto",
	},
	colDefault: {
		width: "auto",
	},
	colActions: {
		width: "auto",
	},
	priorityCell: {
		whiteSpace: "nowrap",
		overflow: "visible",
		textOverflow: "clip",
		maxWidth: "none",
		paddingLeft: 14,
		paddingRight: 14,
	},
	sessionButton: {
		minWidth: 86,
		paddingLeft: 10,
		paddingRight: 10,
	},
	actionIconButton: {
		padding: 6,
	},
	tooltip: {
		backgroundColor: isDark ? theme.palette.background.paper : "#f5f5f9",
		color: isDark ? theme.palette.text.primary : "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: `1px solid ${theme.palette.divider}`,
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
	typeBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		padding: "4px 10px",
		borderRadius: 999,
		fontSize: "0.72rem",
		fontWeight: 700,
		letterSpacing: 0.2,
		border: `1px solid ${theme.palette.divider}`,
	},
	typeBadgeWhatsApp: {
		backgroundColor: isDark ? "rgba(37, 211, 102, 0.16)" : "#e9fbf1",
		color: isDark ? "#7ef3ad" : "#128c4a",
		borderColor: isDark ? "rgba(126, 243, 173, 0.35)" : "#b4e8ca",
	},
	typeBadgeWebchat: {
		backgroundColor: isDark ? "rgba(33, 150, 243, 0.16)" : "#ebf5ff",
		color: isDark ? "#8fc7ff" : "#0f61b8",
		borderColor: isDark ? "rgba(143, 199, 255, 0.35)" : "#b5d8ff",
	},
	typeBadgeFacebook: {
		backgroundColor: isDark ? "rgba(24, 119, 242, 0.18)" : "#eef5ff",
		color: isDark ? "#9ec8ff" : "#1877f2",
		borderColor: isDark ? "rgba(158, 200, 255, 0.35)" : "#b8d6ff",
	},
	typeIcon: {
		fontSize: "1rem",
	},
	officialSection: {
		marginTop: theme.spacing(2),
	},
	officialSectionTitle: {
		marginBottom: theme.spacing(1),
	},
	setupModeContainer: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: theme.spacing(2),
	},
	setupOptionCard: {
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : theme.palette.background.paper,
		cursor: "pointer",
		boxShadow: "none",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow: "none",
		},
	},
	manualForm: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: theme.spacing(2),
	},
	metaFlowBox: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
	},
	webhookText: {
		wordBreak: "break-all",
	},
	});
});

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const getTemplateLanguageCode = templateLanguage => {
		if (typeof templateLanguage === "string") {
			return String(templateLanguage || "").trim() || "pt_BR";
		}

		if (templateLanguage && typeof templateLanguage === "object") {
			return (
				String(
					templateLanguage.code ||
					templateLanguage.language ||
					templateLanguage.locale ||
					""
				).trim() || "pt_BR"
			);
		}

		return "pt_BR";
	};

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [sessionMenuAnchorEl, setSessionMenuAnchorEl] = useState(null);
	const [sessionMenuWhatsApp, setSessionMenuWhatsApp] = useState(null);
	const [apiModalOpen, setApiModalOpen] = useState(false);
	const [apiModalTab, setApiModalTab] = useState("settings");
	const [webchatModalOpen, setWebchatModalOpen] = useState(false);
	const [webchatModalTab, setWebchatModalTab] = useState("settings");
	const [webchatScriptLoading, setWebchatScriptLoading] = useState(false);
	const [webchatScriptPayload, setWebchatScriptPayload] = useState(null);
	const [selectedScriptTab, setSelectedScriptTab] = useState("floating");
	const [scriptColorMode, setScriptColorMode] = useState("preset");
	const [scriptColorPreset, setScriptColorPreset] = useState("flowch");
	const [scriptHueShift, setScriptHueShift] = useState(0);
	const [scriptHexColor, setScriptHexColor] = useState("#0c8c63");
	const [scriptCornerStyle, setScriptCornerStyle] = useState("rounded");
	const [scriptButtonAnimation, setScriptButtonAnimation] = useState("pulse");
	const [scriptFrame, setScriptFrame] = useState(true);
	const [scriptAskName, setScriptAskName] = useState(true);
	const [scriptAskEmail, setScriptAskEmail] = useState(true);
	const [scriptAskWhatsapp, setScriptAskWhatsapp] = useState(false);
	const [webchatAppearanceSaving, setWebchatAppearanceSaving] = useState(false);
	const [webchatAppearanceDirty, setWebchatAppearanceDirty] = useState(false);
	const webchatAppearanceHydratingRef = useRef(false);
	const webchatAppearanceSaveTimerRef = useRef(null);
	const [editingWebchatConnection, setEditingWebchatConnection] = useState(null);
	const [apiSetupMode, setApiSetupMode] = useState(null);
	const [officialConnections, setOfficialConnections] = useState([]);
	const [officialLoading, setOfficialLoading] = useState(false);
	const [officialSaving, setOfficialSaving] = useState(false);
	const [officialTesting, setOfficialTesting] = useState(false);
	const [facebookConnections, setFacebookConnections] = useState([]);
	const [facebookLoading, setFacebookLoading] = useState(false);
	const [facebookSaving, setFacebookSaving] = useState(false);
	const [facebookModalOpen, setFacebookModalOpen] = useState(false);
	const [editingFacebookConnection, setEditingFacebookConnection] = useState(null);
	const [facebookSubscribingId, setFacebookSubscribingId] = useState(null);
	const [facebookSetupMode, setFacebookSetupMode] = useState(null);
	const [facebookOAuthLoading, setFacebookOAuthLoading] = useState(false);
	const [facebookOAuthState, setFacebookOAuthState] = useState(null);
	const [facebookOAuthConnectionId, setFacebookOAuthConnectionId] = useState(null);
	const [facebookOAuthPages, setFacebookOAuthPages] = useState([]);
	const [facebookSelectedPageId, setFacebookSelectedPageId] = useState("");
	const facebookDraftConnectionIdRef = useRef(null);
	const [officialTemplatesLoading, setOfficialTemplatesLoading] = useState(false);
	const [officialTemplatesSaving, setOfficialTemplatesSaving] = useState(false);
	const [officialTemplates, setOfficialTemplates] = useState([]);
	const [officialTemplateRows, setOfficialTemplateRows] = useState([]);
	const [officialTemplateReviewRows, setOfficialTemplateReviewRows] = useState([]);
	const [officialTemplatesMeta, setOfficialTemplatesMeta] = useState(null);
	const [templateAliasesDraft, setTemplateAliasesDraft] = useState({});
	const templateAliasesDraftRef = useRef({});
	const [newTemplateForm, setNewTemplateForm] = useState(createEmptyTemplateForm);
	const [queueOptions, setQueueOptions] = useState([]);
	const [userOptions, setUserOptions] = useState([]);
	const [metaOAuthLoading, setMetaOAuthLoading] = useState(false);
	const [metaOAuthState, setMetaOAuthState] = useState(null);
	const [metaOAuthConnectionId, setMetaOAuthConnectionId] = useState(null);
	const [officialOnboardingMode, setOfficialOnboardingMode] = useState("cloud_api");
	const [officialMetaOptions, setOfficialMetaOptions] = useState({
		cloudApi: { available: true },
		coexistence: { enabled: false, configured: false, available: false },
	});
	const [metaApplications, setMetaApplications] = useState([]);
	const [selectedMetaApplicationId, setSelectedMetaApplicationId] = useState("");
	const [metaApplicationFormOpen, setMetaApplicationFormOpen] = useState(false);
	const [editingMetaApplicationId, setEditingMetaApplicationId] = useState(null);
	const [metaApplicationSaving, setMetaApplicationSaving] = useState(false);
	const [metaApplicationForm, setMetaApplicationForm] = useState(
		createEmptyMetaApplicationForm
	);
	const metaEmbeddedSignupAssetsRef = useRef({});
	const metaDraftConnectionIdRef = useRef(null);
	const [diagnosisModalOpen, setDiagnosisModalOpen] = useState(false);
	const [diagnosisResults, setDiagnosisResults] = useState(null);
	const [diagnosisLoading, setDiagnosisLoading] = useState(false);
	const [diagnosingConnection, setDiagnosingConnection] = useState(null);
	const [editingOfficialConnection, setEditingOfficialConnection] =
		useState(null);
	const [manualConfig, setManualConfig] = useState({
		name: "",
		phoneNumberId: "",
		wabaId: "",
		accessToken: "",
		verifyToken: "",
		queueId: "",
		outboundQueueIds: [],
		defaultUserId: "",
		isDefault: false,
	});
	const [webchatConfig, setWebchatConfig] = useState({
		name: "",
		queueId: "",
		defaultUserId: "",
	});
	const [facebookConfig, setFacebookConfig] = useState({
		name: "",
		pageId: "",
		pageAccessToken: "",
		verifyToken: "",
		queueId: "",
		defaultUserId: "",
		isDefault: false,
	});
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);
	const [deleteModalInfo, setDeleteModalInfo] = useState({
		open: false,
		type: "",
		sourceId: null,
		sourceName: "",
		targetWhatsappId: "",
		requireTarget: false,
		loading: false,
	});

	const visibleQrConnections = (whatsApps || []).filter(
		item => !String(item?.session || "").startsWith("official-")
	);

	const getConnectionType = useCallback(connection => {
		return getConnectionChannel(connection);
	}, []);

	const canShowQrAction = whatsApp => {
		if (getConnectionType(whatsApp) !== "whatsapp") {
			return false;
		}

		const status = normalizeConnectionStatus(whatsApp);
		const hasQrCode = Boolean(whatsApp?.qrcode);

		return hasQrCode || status === "QRCODE";
	};

	const canShowRestartAction = whatsApp => {
		if (getConnectionType(whatsApp) !== "whatsapp") {
			return false;
		}

		const status = normalizeConnectionStatus(whatsApp);

		return isQrCodeConnectionNeedingAttention(whatsApp) && status !== "QRCODE";
	};

	const canShowSessionActions = whatsApp => getConnectionType(whatsApp) === "whatsapp";

	const handleQrAction = whatsApp => {
		handleOpenQrModal(whatsApp);
	};

	const handleOpenSessionMenu = (event, whatsApp) => {
		setSessionMenuAnchorEl(event.currentTarget);
		setSessionMenuWhatsApp(whatsApp);
	};

	const handleCloseSessionMenu = () => {
		setSessionMenuAnchorEl(null);
		setSessionMenuWhatsApp(null);
	};

	const handleRestartWhatsAppSession = async whatsApp => {
		try {
			await api.post(`/whatsappsession/${whatsApp.id}/restart`);
			toast.info(i18n.t("connections.toasts.restarting"));
		} catch (err) {
			toastError(err);
		}
	};

	const handlePauseWhatsAppSession = async whatsApp => {
		try {
			await api.post(`/whatsappsession/${whatsApp.id}/pause`);
			toast.info(i18n.t("connections.toasts.paused"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSyncOpenTicketsHistory = async whatsApp => {
		try {
			const { data } = await api.post(
				`/whatsappsession/${whatsApp.id}/sync-open-tickets-history`,
				{ limit: 100 }
			);
			const toastMessage = i18n.t("connections.toasts.openTicketsHistorySynced", {
					tickets: data?.ticketsCount || 0,
					messages: data?.importedCount || 0,
					failures: data?.failedCount || 0,
				});

			if (Number(data?.failedCount || 0) > 0) {
				toast.warn(toastMessage);
			} else {
				toast.success(toastMessage);
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleSessionMenuAction = async action => {
		const whatsApp = sessionMenuWhatsApp;
		handleCloseSessionMenu();

		if (!whatsApp) return;

		if (action === "qrcode") {
			handleQrAction(whatsApp);
			return;
		}

		if (action === "restart") {
			await handleRestartWhatsAppSession(whatsApp);
			return;
		}

		if (action === "pause") {
			await handlePauseWhatsAppSession(whatsApp);
			return;
		}

		if (action === "syncOpenTicketsHistory") {
			await handleSyncOpenTicketsHistory(whatsApp);
			return;
		}

		if (action === "newQr") {
			await handleRequestNewQrCode(whatsApp);
			return;
		}

		if (action === "disconnect") {
			handleOpenConfirmationModal("disconnect", whatsApp.id);
		}
	};

	const getActiveMigrationTargets = useCallback(
		sourceWhatsappId =>
			(whatsApps || []).filter(item => {
				const status = String(item?.status || "").trim().toUpperCase();
				if (!status || status === "DISCONNECTED") {
					return false;
				}

				return Number(item.id) !== Number(sourceWhatsappId);
			}),
		[whatsApps]
	);

	const resolveOfficialFallbackWhatsappId = useCallback(
		officialConnectionId => {
			const expectedSession = `official-${officialConnectionId}`;
			const fallback = (whatsApps || []).find(
				item => String(item?.session || "") === expectedSession
			);

			return fallback ? Number(fallback.id) : null;
		},
		[whatsApps]
	);

	const loadOfficialConnections = useCallback(async () => {
		setOfficialLoading(true);
		try {
			const { data } = await api.get("/api/whatsapp-official");
			setOfficialConnections(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		} finally {
			setOfficialLoading(false);
		}
	}, []);

	const loadFacebookConnections = useCallback(async () => {
		setFacebookLoading(true);
		try {
			const { data } = await api.get("/api/facebook-messenger");
			setFacebookConnections(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		} finally {
			setFacebookLoading(false);
		}
	}, []);

	const loadQueueOptions = useCallback(async () => {
		try {
			const { data } = await api.get("/queue");
			setQueueOptions(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadUserOptions = useCallback(async () => {
		try {
			const loadedUsers = [];
			let pageNumber = 1;
			let hasMore = true;

			while (hasMore && pageNumber <= 20) {
				const { data } = await api.get("/users", { params: { pageNumber } });
				const pageUsers = Array.isArray(data?.users) ? data.users : [];
				loadedUsers.push(...pageUsers);
				hasMore = Boolean(data?.hasMore);
				pageNumber += 1;
			}

			setUserOptions(loadedUsers);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		loadOfficialConnections();
	}, [loadOfficialConnections]);

	useEffect(() => {
		loadFacebookConnections();
	}, [loadFacebookConnections]);

	useEffect(() => {
		loadQueueOptions();
	}, [loadQueueOptions]);

	useEffect(() => {
		loadUserOptions();
	}, [loadUserOptions]);

	const handleStartWhatsAppSession = async whatsApp => {
		try {
			setSelectedWhatsApp(whatsApp);
			setQrModalOpen(true);
			await api.post(`/whatsappsession/${whatsApp.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsApp => {
		try {
			setSelectedWhatsApp(whatsApp);
			setQrModalOpen(true);
			await api.put(`/whatsappsession/${whatsApp.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConnectionMenu = event => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseConnectionMenu = () => {
		setMenuAnchorEl(null);
	};

	const handleSelectQrCodeConnection = () => {
		handleOpenWhatsAppModal();
		handleCloseConnectionMenu();
	};

	const handleSelectManualQrCodeConnection = async () => {
		await handleCloseApiModal();
		handleOpenWhatsAppModal();
	};

	const handleSelectWebchatConnection = () => {
		setEditingWebchatConnection(null);
		setWebchatModalTab("settings");
		setWebchatScriptPayload(null);
		setWebchatConfig({
			name: "",
			queueId: "",
			defaultUserId: "",
		});
		setWebchatModalOpen(true);
		handleCloseConnectionMenu();
	};

	const handleSelectFacebookConnection = () => {
		setEditingFacebookConnection(null);
		setFacebookSetupMode(null);
		setFacebookOAuthLoading(false);
		setFacebookOAuthState(null);
		setFacebookOAuthConnectionId(null);
		setFacebookOAuthPages([]);
		setFacebookSelectedPageId("");
		facebookDraftConnectionIdRef.current = null;
		setFacebookConfig({
			name: "",
			pageId: "",
			pageAccessToken: "",
			verifyToken: "",
			queueId: "",
			defaultUserId: "",
			isDefault: false,
		});
		setFacebookModalOpen(true);
		handleCloseConnectionMenu();
	};

	const handleCloseFacebookModal = () => {
		setFacebookModalOpen(false);
		setEditingFacebookConnection(null);
		setFacebookSetupMode(null);
		setFacebookOAuthLoading(false);
		setFacebookOAuthState(null);
		setFacebookOAuthConnectionId(null);
		setFacebookOAuthPages([]);
		setFacebookSelectedPageId("");
		facebookDraftConnectionIdRef.current = null;
		setFacebookConfig({
			name: "",
			pageId: "",
			pageAccessToken: "",
			verifyToken: "",
			queueId: "",
			defaultUserId: "",
			isDefault: false,
		});
	};

	const handleEditFacebookConnection = connection => {
		setEditingFacebookConnection(connection);
		setFacebookSetupMode("manual");
		setFacebookOAuthLoading(false);
		setFacebookOAuthState(null);
		setFacebookOAuthConnectionId(null);
		setFacebookOAuthPages([]);
		setFacebookSelectedPageId("");
		setFacebookConfig({
			name: connection?.name || "",
			pageId: connection?.pageId || "",
			pageAccessToken: "",
			verifyToken: connection?.verifyToken || "",
			queueId: connection?.queueId ? String(connection.queueId) : "",
			defaultUserId: connection?.defaultUserId ? String(connection.defaultUserId) : "",
			isDefault: Boolean(connection?.isDefault || connection?.default),
		});
		setFacebookModalOpen(true);
	};

	const handleSelectFacebookManualSetup = () => {
		setFacebookSetupMode("manual");
	};

	const handleFacebookFieldChange = field => event => {
		const value =
			field === "isDefault"
				? Boolean(event?.target?.checked)
				: String(event?.target?.value || "");

		setFacebookConfig(prev => ({
			...prev,
			[field]: value,
		}));
	};

	const handleBackToFacebookSetupOptions = async () => {
		if (facebookDraftConnectionIdRef.current) {
			try {
				await api.delete(`/api/facebook-messenger/${facebookDraftConnectionIdRef.current}`);
			} catch (_err) {
				// Best effort cleanup: draft may already have been completed or removed.
			}
		}

		facebookDraftConnectionIdRef.current = null;
		setEditingFacebookConnection(null);
		setFacebookSetupMode(null);
		setFacebookOAuthLoading(false);
		setFacebookOAuthState(null);
		setFacebookOAuthConnectionId(null);
		setFacebookOAuthPages([]);
		setFacebookSelectedPageId("");
	};

	const validateFacebookConfig = () => {
		if (
			!facebookConfig.name.trim() ||
			!facebookConfig.pageId.trim() ||
			!facebookConfig.verifyToken.trim() ||
			(!editingFacebookConnection?.id && !facebookConfig.pageAccessToken.trim())
		) {
			toast.error(tFacebook("validationRequired"));
			return false;
		}

		return true;
	};

	const handleSaveFacebookConnection = async () => {
		if (!validateFacebookConfig()) {
			return;
		}

		setFacebookSaving(true);
		try {
			const pageAccessToken = facebookConfig.pageAccessToken.trim();
			const shouldSendPageAccessToken =
				Boolean(pageAccessToken) && !/^[*.•]+$/.test(pageAccessToken);
			const payload = {
				name: facebookConfig.name.trim(),
				pageId: facebookConfig.pageId.trim(),
				verifyToken: facebookConfig.verifyToken.trim(),
				queueId: facebookConfig.queueId ? Number(facebookConfig.queueId) : null,
				defaultUserId: facebookConfig.defaultUserId ? Number(facebookConfig.defaultUserId) : null,
				isDefault: Boolean(facebookConfig.isDefault),
			};

			if (shouldSendPageAccessToken) {
				payload.pageAccessToken = pageAccessToken;
			}

			if (editingFacebookConnection?.id) {
				await api.put(`/api/facebook-messenger/${editingFacebookConnection.id}`, payload);
				toast.success(tFacebook("updated"));
			} else {
				await api.post("/api/facebook-messenger", payload);
				toast.success(tFacebook("created"));
			}

			await loadFacebookConnections();
			handleCloseFacebookModal();
		} catch (err) {
			toastError(err);
		} finally {
			setFacebookSaving(false);
		}
	};

	const buildFacebookDraftPayload = () => ({
		name:
			facebookConfig.name.trim() ||
			`Facebook Messenger ${format(new Date(), "dd/MM/yy HH:mm")}`,
		setupMode: "facebook_login",
		queueId: facebookConfig.queueId ? Number(facebookConfig.queueId) : null,
		defaultUserId: facebookConfig.defaultUserId ? Number(facebookConfig.defaultUserId) : null,
		isDefault: Boolean(facebookConfig.isDefault),
		status: "pending",
	});

	const handleStartFacebookMetaSetup = async () => {
		setFacebookOAuthLoading(true);
		setFacebookOAuthPages([]);
		setFacebookSelectedPageId("");

		try {
			const payload = buildFacebookDraftPayload();
			const { data: connection } = editingFacebookConnection?.id
				? await api.put(`/api/facebook-messenger/${editingFacebookConnection.id}`, payload)
				: await api.post("/api/facebook-messenger", payload);

			setEditingFacebookConnection(connection);
			setFacebookConfig(prev => ({
				...prev,
				name: connection?.name || payload.name,
				verifyToken: connection?.verifyToken || prev.verifyToken,
				queueId: connection?.queueId ? String(connection.queueId) : prev.queueId,
				defaultUserId: connection?.defaultUserId ? String(connection.defaultUserId) : prev.defaultUserId,
				isDefault: Boolean(connection?.isDefault || payload.isDefault),
			}));

			if (!editingFacebookConnection?.id) {
				facebookDraftConnectionIdRef.current = connection.id;
			}

			const oauthResponse = await api.get(
				`/api/facebook-messenger/${connection.id}/meta/oauth-url`
			);
			const authUrl = oauthResponse?.data?.authUrl;
			const state = oauthResponse?.data?.state;

			if (!authUrl || !state) {
				toast.error(tFacebook("metaStartFailed"));
				setFacebookOAuthLoading(false);
				return;
			}

			setFacebookOAuthState(state);
			setFacebookOAuthConnectionId(connection.id);

			const popup = window.open(
				authUrl,
				"facebookMessengerSetup",
				"width=980,height=760"
			);

			if (!popup) {
				setFacebookOAuthLoading(false);
				setFacebookOAuthState(null);
				setFacebookOAuthConnectionId(null);
				toast.error(tFacebook("popupBlocked"));
				return;
			}

			toast.info(tFacebook("finishLogin"));
		} catch (err) {
			setFacebookOAuthLoading(false);
			toastError(err);
		}
	};

	const handleCompleteFacebookMetaSetup = async () => {
		if (!facebookOAuthConnectionId || !facebookSelectedPageId) {
			toast.error(tFacebook("selectPage"));
			return;
		}

		const selectedPage = facebookOAuthPages.find(
			page => String(page.id) === String(facebookSelectedPageId)
		);

		if (!selectedPage) {
			toast.error(tFacebook("pageNotFound"));
			return;
		}

		setFacebookOAuthLoading(true);
		try {
			const { data } = await api.post(
				`/api/facebook-messenger/${facebookOAuthConnectionId}/meta/complete`,
				{
					pageId: selectedPage.id,
					pageName: selectedPage.name,
					queueId: facebookConfig.queueId ? Number(facebookConfig.queueId) : null,
					defaultUserId: facebookConfig.defaultUserId ? Number(facebookConfig.defaultUserId) : null,
					isDefault: Boolean(facebookConfig.isDefault),
				}
			);

			const completedConnection = data?.connection || {
				id: facebookOAuthConnectionId,
				name: selectedPage.name,
				provider: "meta_facebook_messenger",
			};
			const diagnosis = data?.diagnosis || null;

			if (diagnosis) {
				setDiagnosingConnection(completedConnection);
				setDiagnosisResults(diagnosis);
				setDiagnosisLoading(false);
				setDiagnosisModalOpen(true);
			}

			if (diagnosis && !diagnosis.ok) {
				toast.warn(tFacebook("diagnosisPending"));
			} else {
				toast.success(data?.message || tFacebook("created"));
			}

			facebookDraftConnectionIdRef.current = null;
			await loadFacebookConnections();
			handleCloseFacebookModal();
		} catch (err) {
			toastError(err);
		} finally {
			setFacebookOAuthLoading(false);
		}
	};

	const handleOpenDeleteFacebookConnectionModal = connection => {
		setDeleteModalInfo({
			open: true,
			type: "facebook",
			sourceId: Number(connection.id),
			sourceName: connection.name,
			targetWhatsappId: "",
			requireTarget: false,
			loading: false,
		});
	};

	const handleDiagnoseFacebookConnection = async connection => {
		setDiagnosingConnection(connection);
		setDiagnosisResults(null);
		setDiagnosisModalOpen(true);
		setDiagnosisLoading(true);

		try {
			const { data } = await api.get(`/api/facebook-messenger/${connection.id}/diagnose`);
			setDiagnosisResults(data);
			await loadFacebookConnections();
		} catch (err) {
			toastError(err);
			setDiagnosisModalOpen(false);
		} finally {
			setDiagnosisLoading(false);
		}
	};

	const handleSubscribeFacebookWebhook = async connection => {
		setFacebookSubscribingId(connection.id);
		setDiagnosingConnection(connection);
		setDiagnosisResults(null);
		setDiagnosisModalOpen(true);
		setDiagnosisLoading(true);

		try {
			const { data } = await api.post(
				`/api/facebook-messenger/${connection.id}/subscribe-webhook`
			);
			toast.success(data?.message || tFacebook("webhookSubscribed"));
			setDiagnosisResults(data?.diagnosis || null);
			await loadFacebookConnections();
		} catch (err) {
			toastError(err);
			try {
				const { data } = await api.get(`/api/facebook-messenger/${connection.id}/diagnose`);
				setDiagnosisResults(data);
			} catch (_diagnoseErr) {
				setDiagnosisModalOpen(false);
			}
		} finally {
			setDiagnosisLoading(false);
			setFacebookSubscribingId(null);
		}
	};

	const persistWebchatAppearanceNow = async () => {
		if (!webchatScriptPayload?.connectionId || webchatAppearanceHydratingRef.current) {
			return;
		}

		if (webchatAppearanceSaveTimerRef.current) {
			clearTimeout(webchatAppearanceSaveTimerRef.current);
			webchatAppearanceSaveTimerRef.current = null;
		}

		const appearancePayload = {
			buttonAnimation: scriptButtonAnimation,
			frame: Boolean(scriptFrame),
			theme: widgetThemeConfig,
			editor: {
				colorMode: scriptColorMode,
				colorPreset: scriptColorPreset,
				hueShift: Number(scriptHueShift || 0),
				hexColor: normalizeHexColor(scriptHexColor),
				cornerStyle: scriptCornerStyle,
			},
			prechat: {
				askName: Boolean(scriptAskName),
				askEmail: Boolean(scriptAskEmail),
				askWhatsapp: Boolean(scriptAskWhatsapp),
			},
		};

		setWebchatAppearanceSaving(true);
		try {
			await api.put(
				`/whatsapp/${webchatScriptPayload.connectionId}/webchat-appearance`,
				{ appearance: appearancePayload }
			);
			setWebchatAppearanceDirty(false);
			toast.success(tWebchat("customizationSaved"));
		} catch (err) {
			toastError(err);
		} finally {
			setWebchatAppearanceSaving(false);
		}
	};

	const handleCloseWebchatModal = async () => {
		setWebchatModalOpen(false);
		setWebchatModalTab("settings");
		setEditingWebchatConnection(null);
		setWebchatScriptPayload(null);
		setWebchatConfig({
			name: "",
			queueId: "",
			defaultUserId: "",
		});
	};

	const handleSaveWebchatConnection = async () => {
		if (!webchatConfig.name.trim()) {
			toast.error("Informe o nome da conexao Webchat.");
			return;
		}

		if (!webchatConfig.queueId) {
			toast.error("Selecione a fila para novos contatos do Webchat.");
			return;
		}

		const queueId = Number(webchatConfig.queueId);
		const basePayload = {
			name: webchatConfig.name.trim(),
			status: "CONNECTED",
			queueIds: [queueId],
			defaultUserId: webchatConfig.defaultUserId
				? Number(webchatConfig.defaultUserId)
				: null,
			isDefault: false,
			session:
				editingWebchatConnection?.session ||
				`webchat-${Date.now()}`,
		};

		try {
			if (editingWebchatConnection?.id) {
				await api.put(`/whatsapp/${editingWebchatConnection.id}`, basePayload);
				toast.success("Conexao Webchat atualizada com sucesso.");
			} else {
				await api.post("/whatsapp/", basePayload);
				toast.success("Conexao Webchat criada com sucesso.");
			}

			handleCloseWebchatModal();
		} catch (err) {
			toastError(err);
		}
	};

	const handleEditWebchatConnection = connection => {
		setEditingWebchatConnection(connection);
		setWebchatModalTab("settings");
		setWebchatScriptPayload(null);
		setWebchatConfig({
			name: connection?.name || "",
			queueId: connection?.queues?.[0]?.id ? String(connection.queues[0].id) : "",
			defaultUserId: connection?.defaultUserId ? String(connection.defaultUserId) : "",
		});
		setWebchatModalOpen(true);
	};

	const loadWebchatScriptPayload = async connection => {
		setWebchatScriptPayload(null);
		setSelectedScriptTab("floating");
		setScriptColorMode("preset");
		setScriptColorPreset("flowch");
		setScriptHueShift(0);
		setScriptHexColor("#0c8c63");
		setScriptCornerStyle("rounded");
		setScriptButtonAnimation("pulse");
		setScriptFrame(true);
		setScriptAskName(true);
		setScriptAskEmail(true);
		setScriptAskWhatsapp(false);
		setWebchatAppearanceSaving(false);
		setWebchatAppearanceDirty(false);
		setWebchatScriptLoading(true);

		try {
			const { data } = await api.get(`/whatsapp/${connection.id}/webchat-script`);
			const editorConfig = data?.appearance?.editor || {};
			webchatAppearanceHydratingRef.current = true;
			setScriptColorMode(String(editorConfig.colorMode || "preset"));
			setScriptColorPreset(String(editorConfig.colorPreset || "flowch"));
			setScriptHueShift(Number(editorConfig.hueShift || 0));
			setScriptHexColor(String(editorConfig.hexColor || "#0c8c63"));
			setScriptCornerStyle(String(editorConfig.cornerStyle || "rounded"));
			setScriptButtonAnimation(String(data?.appearance?.buttonAnimation || "pulse"));
			setScriptFrame(data?.appearance?.frame !== false);
			setScriptAskName(data?.appearance?.prechat?.askName !== false);
			setScriptAskEmail(data?.appearance?.prechat?.askEmail !== false);
			setScriptAskWhatsapp(Boolean(data?.appearance?.prechat?.askWhatsapp));
			setWebchatScriptPayload(data);
			setWebchatAppearanceDirty(false);
		} catch (err) {
			toastError(err);
			setWebchatModalTab("settings");
		} finally {
			setWebchatScriptLoading(false);
		}
	};

	useEffect(() => {
		if (
			!webchatModalOpen ||
			webchatModalTab !== "script" ||
			!editingWebchatConnection?.id ||
			webchatScriptLoading ||
			webchatScriptPayload
		) {
			return;
		}

		loadWebchatScriptPayload(editingWebchatConnection);
	}, [
		webchatModalOpen,
		webchatModalTab,
		editingWebchatConnection,
		webchatScriptLoading,
		webchatScriptPayload,
	]);

	const handleCopySnippet = async value => {
		try {
			await navigator.clipboard.writeText(String(value || ""));
			toast.success("Snippet copiado.");
		} catch (_err) {
			toast.error("Nao foi possivel copiar automaticamente. Copie manualmente.");
		}
	};

	const getSafeEventValue = event => {
		if (event && event.target) {
			return event.target.value;
		}

		return "";
	};

	const handleOpenApiModal = async () => {
		setApiSetupMode(null);
		setOfficialOnboardingMode("cloud_api");
		setApiModalTab("settings");
		setEditingOfficialConnection(null);
		setMetaOAuthLoading(false);
		setMetaOAuthState(null);
		setMetaOAuthConnectionId(null);
		setSelectedMetaApplicationId("");
		setMetaApplicationFormOpen(false);
		setEditingMetaApplicationId(null);
		setMetaApplicationForm(createEmptyMetaApplicationForm());
		metaDraftConnectionIdRef.current = null;
		setOfficialTemplates([]);
		setOfficialTemplateRows([]);
		setOfficialTemplateReviewRows([]);
		setOfficialTemplatesMeta(null);
		setTemplateAliasesDraft({});
		templateAliasesDraftRef.current = {};
		setNewTemplateForm(createEmptyTemplateForm());
		setManualConfig({
			name: "",
			phoneNumberId: "",
			wabaId: "",
			accessToken: "",
			verifyToken: "",
			queueId: "",
			outboundQueueIds: [],
			defaultUserId: "",
			isDefault: false,
		});
		setApiModalOpen(true);
		handleCloseConnectionMenu();
		try {
			const [{ data }, { data: applications }] = await Promise.all([
				api.get("/api/whatsapp-official-meta/options"),
				api.get("/api/meta-applications"),
			]);
			setMetaApplications(Array.isArray(applications) ? applications : []);
			setOfficialMetaOptions(data || {
				cloudApi: { available: true },
				coexistence: { enabled: false, configured: false, available: false },
			});
		} catch (_err) {
			setMetaApplications([]);
			setOfficialMetaOptions({
				cloudApi: { available: true },
				coexistence: { enabled: false, configured: false, available: false },
			});
		}
	};

	async function cleanupAbandonedMetaDraft() {
		const draftConnectionId = metaDraftConnectionIdRef.current;

		if (!draftConnectionId) {
			return;
		}

		metaDraftConnectionIdRef.current = null;

		try {
			await api.delete(`/api/whatsapp-official/${draftConnectionId}`);
			await loadOfficialConnections();
		} catch (_err) {
			// Best effort cleanup: the draft may already have been completed or removed.
		}
	}

	const handleCloseApiModal = async () => {
		await cleanupAbandonedMetaDraft();
		setApiSetupMode(null);
		setOfficialOnboardingMode("cloud_api");
		setApiModalTab("settings");
		setEditingOfficialConnection(null);
		setMetaOAuthLoading(false);
		setMetaOAuthState(null);
		setMetaOAuthConnectionId(null);
		metaDraftConnectionIdRef.current = null;
		setOfficialTemplates([]);
		setOfficialTemplateRows([]);
		setOfficialTemplateReviewRows([]);
		setOfficialTemplatesMeta(null);
		setTemplateAliasesDraft({});
		templateAliasesDraftRef.current = {};
		setApiModalOpen(false);
	};

	const loadOfficialTemplates = useCallback(
		async connectionId => {
			if (!connectionId) {
				setOfficialTemplates([]);
				setOfficialTemplateRows([]);
				setOfficialTemplateReviewRows([]);
				setOfficialTemplatesMeta(null);
				setTemplateAliasesDraft({});
				templateAliasesDraftRef.current = {};
				return;
			}

			setOfficialTemplates([]);
			setOfficialTemplateRows([]);
			setOfficialTemplateReviewRows([]);
			setOfficialTemplatesMeta(null);
			setTemplateAliasesDraft({});
			templateAliasesDraftRef.current = {};
			setOfficialTemplatesLoading(true);
			try {
				const { data } = await api.get(`/api/whatsapp-official/${connectionId}/templates`, {
					params: {
						status: "ALL",
						t: Date.now(),
					},
				});

				const templates = Array.isArray(data?.templates) ? data.templates : [];
				setOfficialTemplates(templates);
				const rows = templates.map((template, index) => {
					const templateName = String(template?.name || "").trim();
					const templateLanguage = getTemplateLanguageCode(template?.language);
					const aliasKey = `${templateName}:${templateLanguage}`;

					return {
						rowId: `${String(connectionId)}:${String(template?.id || "")}:${String(index)}`,
						aliasKey,
						name: templateName,
						language: templateLanguage,
						status: String(template?.status || "-").toUpperCase(),
						category: String(template?.category || "-")
					};
				});

				setOfficialTemplateRows(rows.filter(row => row.status === "APPROVED"));
				setOfficialTemplateReviewRows(rows.filter(row => row.status !== "APPROVED"));
				setOfficialTemplatesMeta({
					wabaId: data?.wabaId || null,
					configuredWabaId: data?.configuredWabaId || null,
					wabaSource: data?.wabaSource || "connection",
					statusFilter: data?.statusFilter || "ALL",
					warning: String(data?.warning || "").trim() || null,
				});

				if (String(data?.warning || "").trim()) {
					toast.error(String(data.warning));
				}

				const aliases = templates.reduce((acc, template) => {
					const templateName = String(template?.name || "").trim();
					const templateLanguage = getTemplateLanguageCode(template?.language);
					if (!templateName) {
						return acc;
					}

					acc[`${templateName}:${templateLanguage}`] = String(
						template?.friendlyName || ""
					).trim();
					return acc;
				}, {});

				setTemplateAliasesDraft(aliases);
				templateAliasesDraftRef.current = aliases;
			} catch (err) {
				setOfficialTemplates([]);
				setOfficialTemplateRows([]);
				setOfficialTemplateReviewRows([]);
				setOfficialTemplatesMeta(null);
				setTemplateAliasesDraft({});
				templateAliasesDraftRef.current = {};
				toastError(err);
			} finally {
				setOfficialTemplatesLoading(false);
			}
		},
		[]
	);

	const handleSyncOfficialTemplates = async () => {
		if (!editingOfficialConnection?.id) {
			return;
		}

		setOfficialTemplatesLoading(true);
		try {
			await api.post(
				`/api/whatsapp-official/${editingOfficialConnection.id}/templates/sync`,
				null,
				{
					params: {
						status: "ALL",
					},
				}
			);
			await loadOfficialTemplates(editingOfficialConnection.id);
			toast.success("Templates sincronizados com sucesso.");
		} catch (err) {
			toastError(err);
		} finally {
			setOfficialTemplatesLoading(false);
		}
	};

	const handleSaveTemplateAliases = async () => {
		if (!editingOfficialConnection?.id) {
			return;
		}

		const aliases = Object.entries(templateAliasesDraftRef.current || {})
			.map(([key, friendlyName]) => {
				const [name, language] = key.split(":");
				return {
					name: String(name || "").trim(),
					language: String(language || "pt_BR").trim(),
					friendlyName: String(friendlyName || "").trim(),
				};
			})
			.filter(item => item.name && item.friendlyName);

		setOfficialTemplatesSaving(true);
		try {
			await api.put(
				`/api/whatsapp-official/${editingOfficialConnection.id}/templates/aliases`,
				{ aliases }
			);
			toast.success("Nomes amigaveis salvos.");
			await loadOfficialTemplates(editingOfficialConnection.id);
		} catch (err) {
			toastError(err);
		} finally {
			setOfficialTemplatesSaving(false);
		}
	};

	const handleCreateTemplate = async () => {
		if (!editingOfficialConnection?.id) {
			return;
		}

		if (!newTemplateForm.name.trim() || !newTemplateForm.bodyText.trim()) {
			toast.error(tOfficial("templateFieldsRequired"));
			return;
		}

		const variables = extractTemplateVariables(newTemplateForm.bodyText);
		const namedVariables = variables.filter(variable => !/^\d+$/.test(variable));
		const positionalVariables = variables.filter(variable => /^\d+$/.test(variable));
		if (namedVariables.length && positionalVariables.length) {
			toast.error(tOfficial("templates.mixedVariablesError"));
			return;
		}
		if (newTemplateForm.parameterFormat === "NAMED" && positionalVariables.length) {
			toast.error(tOfficial("templates.namedFormatError"));
			return;
		}
		if (newTemplateForm.parameterFormat === "POSITIONAL" && namedVariables.length) {
			toast.error(tOfficial("templates.positionalFormatError"));
			return;
		}

		const missingExamples =
			newTemplateForm.parameterFormat === "NAMED"
				? namedVariables.filter(variable => !String(newTemplateForm.parameterExamples?.[variable] || "").trim())
				: positionalVariables.filter(variable => !String(newTemplateForm.positionalExamples?.[Number(variable) - 1] || "").trim());
		if (missingExamples.length) {
			toast.error(tOfficial("templates.examplesRequired"));
			return;
		}

		setOfficialTemplatesSaving(true);
		try {
			await api.post(`/api/whatsapp-official/${editingOfficialConnection.id}/templates`, {
				name: newTemplateForm.name.trim(),
				friendlyName: newTemplateForm.friendlyName.trim(),
				language: newTemplateForm.language.trim(),
				category: newTemplateForm.category.trim(),
				bodyText: newTemplateForm.bodyText.trim(),
				parameterFormat: variables.length ? newTemplateForm.parameterFormat : undefined,
				parameterExamples: newTemplateForm.parameterExamples,
				positionalExamples: newTemplateForm.positionalExamples,
			});

			toast.success(tOfficial("templateSubmitted"));
			setNewTemplateForm(createEmptyTemplateForm());
			await loadOfficialTemplates(editingOfficialConnection.id);
		} catch (err) {
			toastError(err);
		} finally {
			setOfficialTemplatesSaving(false);
		}
	};

	const handleManualFieldChange = (field, value) => {
		setManualConfig(prev => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSelectManualSetup = () => {
		setApiSetupMode("manual_choice");
	};

	const handleSelectMetaSetup = () => {
		setApiSetupMode("meta_mode_choice");
	};

	const handleSelectMetaOnboardingMode = mode => {
		if (mode === "coexistence" && !officialMetaOptions?.coexistence?.available) {
			toast.info(tOfficial("coexistenceUnavailable"));
			return;
		}
		setOfficialOnboardingMode(mode === "coexistence" ? "coexistence" : "cloud_api");
		setApiSetupMode("meta_embedded_signup");
	};

	const hydrateManualConfigFromConnection = connection => {
		setOfficialOnboardingMode(connection?.onboardingMode || "cloud_api");
		setSelectedMetaApplicationId(
			connection?.metaApplicationId ? String(connection.metaApplicationId) : ""
		);
		setManualConfig({
			name: connection?.name || "",
			phoneNumberId: connection?.phoneNumberId || "",
			wabaId: connection?.wabaId || "",
			accessToken: connection?.accessToken || "",
			verifyToken: connection?.verifyToken || "",
			queueId: connection?.queueId ? String(connection.queueId) : "",
			outboundQueueIds: Array.isArray(connection?.outboundQueueIds) ? connection.outboundQueueIds : [],
			defaultUserId: connection?.defaultUserId ? String(connection.defaultUserId) : "",
			isDefault: Boolean(connection?.isDefault),
		});
	};

	const loadMetaApplications = useCallback(async () => {
		const { data } = await api.get("/api/meta-applications");
		const applications = Array.isArray(data) ? data : [];
		setMetaApplications(applications);
		return applications;
	}, []);

	const handleNewMetaApplication = () => {
		setEditingMetaApplicationId(null);
		setMetaApplicationForm(createEmptyMetaApplicationForm());
		setMetaApplicationFormOpen(true);
	};

	const handleMetaApplicationFieldChange = (field, value) => {
		setMetaApplicationForm(current => ({
			...current,
			[field]: value,
		}));
	};

	const handleEditMetaApplication = application => {
		setEditingMetaApplicationId(application.id);
		setMetaApplicationForm({
			name: application.name || "",
			appId: application.appId || "",
			appSecret: "",
			verifyToken: "",
			embeddedSignupConfigId: application.embeddedSignupConfigId || "",
			coexistenceConfigId: application.coexistenceConfigId || "",
			appMode: application.appMode || "live",
		});
		setMetaApplicationFormOpen(true);
	};

	const handleSaveMetaApplication = async () => {
		if (
			!metaApplicationForm.name.trim() ||
			!metaApplicationForm.appId.trim() ||
			(!editingMetaApplicationId && !metaApplicationForm.appSecret.trim())
		) {
			toast.error(tOfficial("metaApplicationRequired"));
			return;
		}
		if (!hasUsableNewVerifyToken(metaApplicationForm.verifyToken, Boolean(editingMetaApplicationId))) {
			toast.error(tOfficial("metaApplicationVerifyTokenRequired"));
			return;
		}
		setMetaApplicationSaving(true);
		try {
			const payload = {
				name: metaApplicationForm.name,
				appId: metaApplicationForm.appId,
				embeddedSignupConfigId: metaApplicationForm.embeddedSignupConfigId,
				coexistenceConfigId: metaApplicationForm.coexistenceConfigId,
				appMode: metaApplicationForm.appMode,
				...(metaApplicationForm.appSecret.trim()
					? { appSecret: metaApplicationForm.appSecret }
					: {}),
				...(metaApplicationForm.verifyToken.trim()
					? { verifyToken: metaApplicationForm.verifyToken }
					: {}),
				ownerType: "company",
			};
			const { data } = editingMetaApplicationId
				? await api.put(`/api/meta-applications/${editingMetaApplicationId}`, payload)
				: await api.post("/api/meta-applications", payload);
			await loadMetaApplications();
			setSelectedMetaApplicationId(String(data.id));
			setMetaApplicationFormOpen(false);
			setEditingMetaApplicationId(null);
			setMetaApplicationForm(createEmptyMetaApplicationForm());
			toast.success(tOfficial("metaApplicationSaved"));
		} catch (err) {
			toastError(err);
		} finally {
			setMetaApplicationSaving(false);
		}
	};

	const handleDeleteMetaApplication = async () => {
		if (!editingMetaApplicationId) return;
		if (!window.confirm(tOfficial("metaApplicationDeleteConfirm"))) return;
		setMetaApplicationSaving(true);
		try {
			await api.delete(`/api/meta-applications/${editingMetaApplicationId}`);
			await loadMetaApplications();
			setSelectedMetaApplicationId("");
			setMetaApplicationFormOpen(false);
			setEditingMetaApplicationId(null);
			setMetaApplicationForm(createEmptyMetaApplicationForm());
			toast.success(tOfficial("metaApplicationRemoved"));
		} catch (err) {
			toastError(err);
		} finally {
			setMetaApplicationSaving(false);
		}
	};

	useEffect(() => {
		const handleMetaOAuthMessage = async event => {
			const eventOrigin = String(event?.origin || "");
			let trustedOrigin = eventOrigin === window.location.origin;
			try {
				const hostname = new URL(eventOrigin).hostname.toLowerCase();
				trustedOrigin =
					trustedOrigin ||
					hostname === "facebook.com" ||
					hostname.endsWith(".facebook.com");
			} catch (_err) {
				trustedOrigin = false;
			}
			if (!trustedOrigin) return;

			let payload = event?.data || {};

			if (typeof payload === "string") {
				try {
					payload = JSON.parse(payload);
				} catch (_err) {
					payload = {};
				}
			}

			if (payload?.type === "WA_EMBEDDED_SIGNUP") {
				const eventName = String(payload?.event || "").toUpperCase();
				const embeddedData = payload?.data || {};

				if (eventName.startsWith("FINISH")) {
					const nextEmbeddedAssets = {
						wabaId: embeddedData?.waba_id || embeddedData?.wabaId || "",
						phoneNumberId:
							embeddedData?.phone_number_id || embeddedData?.phoneNumberId || "",
						businessId: embeddedData?.business_id || embeddedData?.businessId || "",
					};
					metaEmbeddedSignupAssetsRef.current = nextEmbeddedAssets;
				}

				if (eventName === "CANCEL" || eventName === "CANCELLED") {
					toast.info(tOfficial("metaCancelled"));
					setMetaOAuthLoading(false);
					await cleanupAbandonedMetaDraft();
				}

				return;
			}

			if (payload?.source !== "flowch_meta_oauth") {
				return;
			}

			if (!metaOAuthState || payload.state !== metaOAuthState) {
				return;
			}

			if (payload.error) {
				toast.error(payload.error_description || tOfficial("metaAuthFailed"));
				setMetaOAuthLoading(false);
				setMetaOAuthState(null);
				setMetaOAuthConnectionId(null);
				await cleanupAbandonedMetaDraft();
				return;
			}

			if (!payload.code || !metaOAuthConnectionId) {
				toast.error(tOfficial("metaReturnFailed"));
				setMetaOAuthLoading(false);
				setMetaOAuthState(null);
				setMetaOAuthConnectionId(null);
				await cleanupAbandonedMetaDraft();
				return;
			}

			setMetaOAuthLoading(true);
			try {
				const storedEmbeddedAssets = metaEmbeddedSignupAssetsRef.current || {};
				const embeddedAssets = {
					wabaId:
						payload.wabaId ||
						payload.waba_id ||
						storedEmbeddedAssets.wabaId ||
						"",
					phoneNumberId:
						payload.phoneNumberId ||
						payload.phone_number_id ||
						storedEmbeddedAssets.phoneNumberId ||
						"",
					businessId:
						payload.businessId ||
						payload.business_id ||
						storedEmbeddedAssets.businessId ||
						"",
				};
				const { data } = await api.post(
					`/api/whatsapp-official/${metaOAuthConnectionId}/meta/complete`,
					{
						code: payload.code,
						state: payload.state,
						onboardingMode: officialOnboardingMode,
						...embeddedAssets,
					}
				);

				if (data?.diagnosis) {
					setDiagnosingConnection(data?.connection || {
						id: metaOAuthConnectionId,
						name: tOfficial("diagnosisConnectionName"),
					});
					setDiagnosisResults(data.diagnosis);
					setDiagnosisLoading(false);
					setDiagnosisModalOpen(true);
				}

				const diagnosisOk = data?.diagnosis
					? Object.values(data.diagnosis).every(check => Boolean(check?.valid))
					: true;

				if (diagnosisOk) {
					toast.success(data?.message || tOfficial("created"));
				} else {
					toast.warn(tOfficial("diagnosisPending"));
				}

				metaDraftConnectionIdRef.current = null;
				if (data?.connection) {
					setEditingOfficialConnection(data.connection);
					hydrateManualConfigFromConnection(data.connection);
				}
				await loadOfficialConnections();
			} catch (err) {
				toastError(err);
			} finally {
				setMetaOAuthLoading(false);
				setMetaOAuthState(null);
				setMetaOAuthConnectionId(null);
				metaEmbeddedSignupAssetsRef.current = {};
			}
		};

		window.addEventListener("message", handleMetaOAuthMessage);

		return () => {
			window.removeEventListener("message", handleMetaOAuthMessage);
		};
	}, [loadOfficialConnections, metaOAuthConnectionId, metaOAuthState, officialOnboardingMode]);

	useEffect(() => {
		const handleFacebookOAuthMessage = async event => {
			let payload = event?.data || {};

			if (typeof payload === "string") {
				try {
					payload = JSON.parse(payload);
				} catch (_err) {
					payload = {};
				}
			}

			if (payload?.source !== "flowch_facebook_oauth") {
				return;
			}

			if (!facebookOAuthState || payload.state !== facebookOAuthState) {
				return;
			}

			if (payload.error) {
				toast.error(payload.error_description || tOfficial("metaAuthFailed"));
				setFacebookOAuthLoading(false);
				setFacebookOAuthState(null);
				setFacebookOAuthConnectionId(null);
				return;
			}

			if (!payload.code || !facebookOAuthConnectionId) {
				toast.error(tOfficial("metaReturnFailed"));
				setFacebookOAuthLoading(false);
				setFacebookOAuthState(null);
				setFacebookOAuthConnectionId(null);
				return;
			}

			setFacebookOAuthLoading(true);
			try {
				const { data } = await api.post(
					`/api/facebook-messenger/${facebookOAuthConnectionId}/meta/pages`,
					{
						code: payload.code,
						state: payload.state,
					}
				);

				const pages = Array.isArray(data?.pages) ? data.pages : [];
				setFacebookOAuthPages(pages);
				setFacebookSelectedPageId(pages?.[0]?.id ? String(pages[0].id) : "");
				toast.success(tFacebook("loginCompleted"));
			} catch (err) {
				toastError(err);
			} finally {
				setFacebookOAuthLoading(false);
			}
		};

		window.addEventListener("message", handleFacebookOAuthMessage);

		return () => {
			window.removeEventListener("message", handleFacebookOAuthMessage);
		};
	}, [facebookOAuthConnectionId, facebookOAuthState]);

	const handleBackToSetupOptions = async () => {
		await cleanupAbandonedMetaDraft();
		setApiSetupMode(null);
		setEditingOfficialConnection(null);
		setMetaOAuthLoading(false);
		setMetaOAuthState(null);
		setMetaOAuthConnectionId(null);
		metaEmbeddedSignupAssetsRef.current = {};
	};

	const validateManualConfig = () => {
		if (
			!manualConfig.name ||
			!manualConfig.phoneNumberId ||
			!manualConfig.wabaId ||
			(!manualConfig.accessToken && !editingOfficialConnection?.accessTokenConfigured) ||
			(!manualConfig.verifyToken && !editingOfficialConnection?.verifyTokenConfigured) ||
			!manualConfig.queueId
		) {
			toast.error(tOfficial("manualRequired"));
			return false;
		}

		return true;
	};

	const saveManualConnection = async () => {
		if (!validateManualConfig()) {
			return null;
		}

		setOfficialSaving(true);

		try {
			const payload = buildManualConnectionPayload(manualConfig, editingOfficialConnection, selectedMetaApplicationId);
			const providerConfigurationChanged = hasManualProviderChanges(payload, editingOfficialConnection);

			const { data } = editingOfficialConnection?.id
				? await api.put(
						providerConfigurationChanged
							? `/api/whatsapp-official/${editingOfficialConnection.id}`
							: `/api/whatsapp-official/${editingOfficialConnection.id}/routing`,
						payload
				  )
				: await api.post("/api/whatsapp-official", payload);

			toast.success(tOfficial("saved"));
			await loadOfficialConnections();
			setEditingOfficialConnection(data);
			return data;
		} catch (err) {
			toastError(err);
			return null;
		} finally {
			setOfficialSaving(false);
		}
	};

	const handleSaveManualConnection = async () => {
		await saveManualConnection();
	};

	const handleTestManualConnection = async () => {
		setOfficialTesting(true);
		try {
			let connectionId = editingOfficialConnection?.id;

			if (!connectionId) {
				const saved = await saveManualConnection();
				connectionId = saved?.id;
			}

			if (!connectionId) {
				return;
			}

			await api.post(`/api/whatsapp-official/${connectionId}/test`);
			toast.success(tOfficial("tested"));
			await loadOfficialConnections();
		} catch (err) {
			toastError(err);
		} finally {
			setOfficialTesting(false);
		}
	};

	const handleStartMetaSetup = async () => {
		if (!manualConfig.queueId) {
			toast.error(tOfficial("selectQueueRequired"));
			return;
		}

		setOfficialSaving(true);
		try {
			const payload = {
				name:
					editingOfficialConnection?.name ||
					`Meta Embedded Signup ${format(new Date(), "dd/MM/yy HH:mm")}`,
				setupMode: "meta_embedded_signup",
				onboardingMode: officialOnboardingMode,
				queueId: Number(manualConfig.queueId),
				outboundQueueIds: manualConfig.outboundQueueIds,
				defaultUserId: manualConfig.defaultUserId
					? Number(manualConfig.defaultUserId)
					: null,
				isDefault: Boolean(manualConfig.isDefault),
				status: "pending",
				metaApplicationId: selectedMetaApplicationId
					? Number(selectedMetaApplicationId)
					: null,
			};

			const providerConfigurationChanged = editingOfficialConnection?.id && [
				[manualConfig.phoneNumberId, editingOfficialConnection.phoneNumberId],
				[manualConfig.wabaId, editingOfficialConnection.wabaId],
				[manualConfig.accessToken, editingOfficialConnection.accessToken],
				[manualConfig.verifyToken, editingOfficialConnection.verifyToken],
				[selectedMetaApplicationId, editingOfficialConnection.metaApplicationId],
			].some(([nextValue, currentValue]) => String(nextValue || "") !== String(currentValue || ""));

			const { data } = editingOfficialConnection?.id
				? await api.put(
						providerConfigurationChanged
							? `/api/whatsapp-official/${editingOfficialConnection.id}`
							: `/api/whatsapp-official/${editingOfficialConnection.id}/routing`,
						payload
			  	  )
				: await api.post("/api/whatsapp-official", payload);

			setEditingOfficialConnection(data);
			if (!editingOfficialConnection?.id) {
				metaDraftConnectionIdRef.current = data.id;
			}

			setMetaOAuthLoading(true);
			const oauthResponse = await api.get(
				`/api/whatsapp-official/${data.id}/meta/oauth-url`,
				{ params: { onboardingMode: officialOnboardingMode } }
			);

			const authUrl = oauthResponse?.data?.authUrl;
			const state = oauthResponse?.data?.state;

			if (!authUrl || !state) {
				toast.error(tOfficial("metaStartFailed"));
				setMetaOAuthLoading(false);
				setMetaOAuthState(null);
				setMetaOAuthConnectionId(null);
				await cleanupAbandonedMetaDraft();
				return;
			}

			setMetaOAuthState(state);
			setMetaOAuthConnectionId(data.id);
			metaEmbeddedSignupAssetsRef.current = {};

			const popup = window.open(
				authUrl,
				"metaEmbeddedSignup",
				"width=980,height=760"
			);

			if (!popup) {
				setMetaOAuthLoading(false);
				setMetaOAuthState(null);
				setMetaOAuthConnectionId(null);
				metaEmbeddedSignupAssetsRef.current = {};
				await cleanupAbandonedMetaDraft();
				toast.error(tFacebook("popupBlocked"));
				return;
			}

			toast.info(tOfficial("finishMetaSetup"));
		} catch (err) {
			toastError(err);
			setMetaOAuthLoading(false);
			setMetaOAuthState(null);
			setMetaOAuthConnectionId(null);
			metaEmbeddedSignupAssetsRef.current = {};
			await cleanupAbandonedMetaDraft();
		} finally {
			setOfficialSaving(false);
		}
	};

	const handleEditOfficialConnection = connection => {
		loadMetaApplications().catch(() => setMetaApplications([]));
		setApiModalOpen(true);
		setApiModalTab("settings");
		setOfficialTemplates([]);
		setOfficialTemplateRows([]);
		setOfficialTemplateReviewRows([]);
		setOfficialTemplatesMeta(null);
		setTemplateAliasesDraft({});
		templateAliasesDraftRef.current = {};
		setEditingOfficialConnection(connection);
		setApiSetupMode("manual");
		setMetaOAuthLoading(false);
		setMetaOAuthState(null);
		setMetaOAuthConnectionId(null);
		metaEmbeddedSignupAssetsRef.current = {};
		hydrateManualConfigFromConnection(connection);
	};

	useEffect(() => {
		if (!apiModalOpen || apiModalTab !== "templates") {
			return;
		}

		if (!editingOfficialConnection?.id) {
			return;
		}

		loadOfficialTemplates(editingOfficialConnection.id);
	}, [apiModalOpen, apiModalTab, editingOfficialConnection, loadOfficialTemplates]);

	const handleOpenDeleteWhatsAppModal = whatsApp => {
		setDeleteModalInfo({
			open: true,
			type: "qr",
			sourceId: Number(whatsApp.id),
			sourceName: whatsApp.name,
			targetWhatsappId: "",
			requireTarget: false,
			loading: false,
		});
	};

	const handleOpenDeleteOfficialConnectionModal = connection => {
		setDeleteModalInfo({
			open: true,
			type: "official",
			sourceId: Number(connection.id),
			sourceName: connection.name,
			targetWhatsappId: "",
			requireTarget: false,
			loading: false,
		});
	};

	const handleCloseDeleteModal = () => {
		setDeleteModalInfo({
			open: false,
			type: "",
			sourceId: null,
			sourceName: "",
			targetWhatsappId: "",
			requireTarget: false,
			loading: false,
		});
	};

	const handleSubmitDeleteModal = async () => {
		if (!Number(deleteModalInfo.sourceId)) {
			toast.error("Nao foi possivel localizar a conexao base para migracao.");
			return;
		}

		if (deleteModalInfo.requireTarget && !deleteModalInfo.targetWhatsappId) {
			toast.error("Selecione uma conexao ativa para migrar os tickets abertos.");
			return;
		}

		setDeleteModalInfo(prev => ({ ...prev, loading: true }));

		try {
			const endpoint =
				deleteModalInfo.type === "official"
					? `/api/whatsapp-official/${deleteModalInfo.sourceId}`
					: deleteModalInfo.type === "facebook"
						? `/api/facebook-messenger/${deleteModalInfo.sourceId}`
						: `/whatsapp/${deleteModalInfo.sourceId}`;

			const params = {};
			if (deleteModalInfo.targetWhatsappId) {
				params.targetWhatsappId = Number(deleteModalInfo.targetWhatsappId);
			}

			const { data } = await api.delete(endpoint, { params });
			const migratedTickets = Number(data?.migratedTickets || 0);

			if (migratedTickets > 0) {
				toast.success(`Conexao removida. ${migratedTickets} ticket(s) aberto(s) migrado(s).`);
			} else {
				toast.success(i18n.t("connections.toasts.deleted"));
			}

			handleCloseDeleteModal();
			if (deleteModalInfo.type === "official") {
				await loadOfficialConnections();
			}
			if (deleteModalInfo.type === "facebook") {
				await loadFacebookConnections();
			}
		} catch (err) {
			const rawError = String(err?.response?.data?.error || "");
			if (rawError.startsWith("ERR_WAPP_MIGRATION_REQUIRED")) {
				setDeleteModalInfo(prev => ({ ...prev, requireTarget: true, loading: false }));
				toast.info("Esta conexao possui tickets abertos. Selecione uma conexao ativa para migrar.");
				return;
			}

			if (rawError.startsWith("ERR_CONNECTION_HAS_OPEN_TICKETS")) {
				setDeleteModalInfo(prev => ({ ...prev, loading: false }));
				toast.info("Esta conexao possui atendimentos abertos. Finalize-os antes de excluir.");
				return;
			}

			setDeleteModalInfo(prev => ({ ...prev, loading: false }));
			toastError(err);
		}
	};

	const handleDiagnoseOfficialConnection = async connection => {

		setDiagnosingConnection(connection);
		setDiagnosisResults(null);
		setDiagnosisModalOpen(true);
		setDiagnosisLoading(true);

		try {
			const { data } = await api.get(`/api/whatsapp-official/${connection.id}/diagnose`);
			setDiagnosisResults(data);
		} catch (err) {
			toastError(err);
			setDiagnosisModalOpen(false);
		} finally {
			setDiagnosisLoading(false);
		}
	};

	const handleRetestDiagnosingConnection = async () => {
		if (!diagnosingConnection) {
			return;
		}

		if (diagnosingConnection.provider === "meta_facebook_messenger") {
			await handleDiagnoseFacebookConnection(diagnosingConnection);
			return;
		}

		await handleDiagnoseOfficialConnection(diagnosingConnection);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
				toast.info(i18n.t("connections.toasts.disconnected"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalOpen(false);
		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderStatusToolTips = whatsApp => {
		const connectionType = getConnectionType(whatsApp);
		const status = normalizeConnectionStatus(whatsApp);

		if (connectionType === "webchat") {
			return (
				<div className={classes.customTableCell}>
					<CustomToolTip title="Webchat ativo">
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				</div>
			);
		}

		return (
			<div className={classes.customTableCell}>
				{status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{status === "QRCODE" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(status === "TIMEOUT" || status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	const renderOfficialStatusIcon = connection => {
		const { status } = connection;
		return (
			<div className={classes.customTableCell}>
				{status === "connected" && (
					<CustomToolTip title="Conectado">
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{status === "error" && (
					<CustomToolTip title="Erro na conexão" content="Teste a conexão para verificar o problema.">
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{(status === "pending" || !status) && (
					<CustomToolTip title="Aguardando configuração">
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	const renderConnectionTypeBadge = type => {
		if (type === "webchat") {
			return (
				<span className={`${classes.typeBadge} ${classes.typeBadgeWebchat}`}>
					<Public className={classes.typeIcon} />
					Webchat
				</span>
			);
		}

		if (type === "facebook") {
			return (
				<span className={`${classes.typeBadge} ${classes.typeBadgeFacebook}`}>
					<Facebook className={classes.typeIcon} />
					Facebook
				</span>
			);
		}

		return (
			<span className={`${classes.typeBadge} ${classes.typeBadgeWhatsApp}`}>
				<WhatsApp className={classes.typeIcon} />
				WhatsApp
			</span>
		);
	};

	const renderConnectionTypeIcon = type => {
		if (type === "webchat") {
			return <Public className={classes.nameTypeIcon} />;
		}

		if (type === "facebook") {
			return <Facebook className={classes.nameTypeIcon} />;
		}

		return <WhatsApp className={classes.nameTypeIcon} />;
	};

	const selectedScriptLabel =
		selectedScriptTab === "floating"
			? "Botao flutuante"
			: selectedScriptTab === "inline"
				? "Embed inline"
				: "Painel lateral";

	const selectedScriptPlacement =
		selectedScriptTab === "floating"
			? "Cole o snippet antes de </body> no template global do site (rodape/layout principal), para aparecer em todas as paginas."
			: selectedScriptTab === "inline"
				? "Cole no ponto exato da pagina onde o chat deve aparecer fixo no conteudo. Este snippet ja inclui o container #flowch-webchat-container."
				: "Cole o snippet antes de </body> no template global do site para abrir um painel lateral em todas as paginas.";

	const selectedPresetColor =
		SCRIPT_COLOR_PRESETS.find(item => item.value === scriptColorPreset) ||
		SCRIPT_COLOR_PRESETS[0];

	const baseColor =
		scriptColorMode === "hex"
			? normalizeHexColor(scriptHexColor, selectedPresetColor.hex)
			: scriptColorMode === "neutral"
				? "#64748b"
				: selectedPresetColor.hex;

	const primaryThemeColor =
		scriptColorMode === "neutral"
			? "#64748b"
			: scriptColorMode === "hex"
				? baseColor
				: shiftHexHue(baseColor, scriptHueShift);
	const secondaryThemeColor = adjustHexLightness(primaryThemeColor, 8);
	const borderThemeColor = adjustHexLightness(primaryThemeColor, 28);
	const backgroundThemeColor =
		scriptColorMode === "neutral"
			? "#f8fafc"
			: "#f8fafc";

	const widgetThemeConfig = {
		primary: primaryThemeColor,
		primary2: secondaryThemeColor,
		background: backgroundThemeColor,
		surface: "#ffffff",
		text: "#0f172a",
		muted: "#64748b",
		border: borderThemeColor,
		radius: scriptCornerStyle === "square" ? 2 : 18,
		buttonRadius: scriptCornerStyle === "square" ? 8 : 18,
	};

	useEffect(() => {
		if (!webchatModalOpen || webchatModalTab !== "script" || !webchatScriptPayload?.connectionId) {
			return;
		}

		if (webchatAppearanceHydratingRef.current) {
			webchatAppearanceHydratingRef.current = false;
			return;
		}

		setWebchatAppearanceDirty(true);
	}, [
		webchatModalOpen,
		webchatModalTab,
		webchatScriptPayload,
		scriptButtonAnimation,
		scriptColorMode,
		scriptColorPreset,
		scriptHueShift,
		scriptHexColor,
		scriptCornerStyle,
		scriptFrame,
		scriptAskName,
		scriptAskEmail,
		scriptAskWhatsapp,
	]);

	const buildSnippetForLayout = layout => {
		if (!webchatScriptPayload) {
			return "";
		}

		const baseConfig = `token: "${webchatScriptPayload.token}", connectionId: ${webchatScriptPayload.connectionId}, apiBaseUrl: "${webchatScriptPayload.baseUrl}"`;

		if (layout === "inline") {
			return `<div id="flowch-webchat-container"></div>\n<script src="${webchatScriptPayload.baseUrl}/public/webchat/widget.js" defer></script>\n<script>window.FlowchWebchat = window.FlowchWebchat || { queue: [] }; window.FlowchWebchat.queue.push({ action: "init", ${baseConfig}, layout: "inline", container: "#flowch-webchat-container" });</script>`;
		}

		if (layout === "panel") {
			return `<script src="${webchatScriptPayload.baseUrl}/public/webchat/widget.js" defer></script>\n<script>window.FlowchWebchat = window.FlowchWebchat || { queue: [] }; window.FlowchWebchat.queue.push({ action: "init", ${baseConfig}, layout: "panel", position: "right" });</script>`;
		}

		return `<script src="${webchatScriptPayload.baseUrl}/public/webchat/widget.js" defer></script>\n<script>window.FlowchWebchat = window.FlowchWebchat || { queue: [] }; window.FlowchWebchat.queue.push({ action: "init", ${baseConfig}, layout: "floating" });</script>`;
	};

	const selectedSnippet = buildSnippetForLayout(selectedScriptTab);
	const cs = "connectionSetup";
	const tcs = key => i18n.t(`${cs}.${key}`);
	const tFacebook = key => i18n.t(`${cs}.facebook.${key}`);
	const tOfficial = (key, options) => i18n.t(`${cs}.whatsappOfficial.${key}`, options);
	const tWebchat = key => i18n.t(`${cs}.webchat.${key}`);
	const tDiagnosis = key => i18n.t(`${cs}.diagnosis.${key}`);
	const newTemplateVariables = extractTemplateVariables(newTemplateForm.bodyText);
	const newTemplateNamedVariables = newTemplateVariables.filter(variable => !/^\d+$/.test(variable));
	const newTemplatePositionalVariables = newTemplateVariables
		.filter(variable => /^\d+$/.test(variable))
		.sort((left, right) => Number(left) - Number(right));
	const tDiagnosisLabel = key => i18n.t(`${cs}.diagnosis.labels.${key}`);

	const multiLayoutSnippet = webchatScriptPayload
		? `<div id="flowch-webchat-inline-test"></div>\n<script src="${webchatScriptPayload.baseUrl}/public/webchat/widget.js"></script>\n<script>\nwindow.FlowchWebchat = window.FlowchWebchat || {};\nif (typeof window.FlowchWebchat.init === "function") {\n  const baseConfig = {\n    token: "${webchatScriptPayload.token}",\n    connectionId: ${webchatScriptPayload.connectionId},\n    apiBaseUrl: "${webchatScriptPayload.baseUrl}"\n  };\n\n  window.FlowchWebchat.init({ ...baseConfig, layout: "inline", container: "#flowch-webchat-inline-test" });\n  window.FlowchWebchat.init({ ...baseConfig, layout: "floating", position: "left" });\n  window.FlowchWebchat.init({ ...baseConfig, layout: "panel", position: "right" });\n}\n</script>`
		: "";

	const diagnosisChecks = Array.isArray(diagnosisResults?.checks)
		? diagnosisResults.checks.map(check => ({
				label: ({
					pageId: "Page ID",
					verifyToken: "Verify Token",
					webhookUrl: tDiagnosisLabel("webhookUrl"),
					secrets: tDiagnosisLabel("secrets"),
					pageAccessToken: "Page Access Token",
					pageTokenMatch: tDiagnosisLabel("pageTokenMatch"),
					pageWebhookSubscription: tDiagnosisLabel("pageWebhookSubscription"),
					recentInbound: tDiagnosisLabel("recentInbound"),
					diagnose: tDiagnosisLabel("diagnose"),
				}[check.key] || check.key || tDiagnosisLabel("defaultCheck")),
				valid: Boolean(check.ok),
				detail: check.ok ? check.message : null,
				error: !check.ok ? check.message : null,
		  }))
		: diagnosisResults
			? [
					{
						label: "Access Token",
						check: diagnosisResults.token,
						detail: diagnosisResults.token?.details
							? `App: ${diagnosisResults.token.details.name || diagnosisResults.token.details.id}`
							: null,
					},
					{
						label: "Phone Number ID",
						check: diagnosisResults.phoneNumber,
						detail: diagnosisResults.phoneNumber?.details
							? `${diagnosisResults.phoneNumber.details.verified_name || ""} - ${diagnosisResults.phoneNumber.details.display_phone_number || ""}`
							: null,
					},
					{
						label: "WABA ID",
						check: diagnosisResults.waba,
						detail: diagnosisResults.waba?.details
							? `${diagnosisResults.waba.details.name || diagnosisResults.waba.details.id}`
							: null,
					},
					{
						label: tDiagnosisLabel("webhookMeta"),
						check: diagnosisResults.webhook || {
							valid: false,
							error: tDiagnosis("webhookUnavailable"),
							details: null,
						},
						detail: diagnosisResults.webhook?.details
							? i18n.t(`${cs}.diagnosis.expectedVsMeta`, {
									expected: diagnosisResults.webhook.details.expectedCallbackUrl || "-",
									meta: diagnosisResults.webhook.details.metaAppCallbackUrl || "-",
									interpolation: { escapeValue: false },
							  })
							: null,
					},
			  ].map(({ label, check, detail }) => ({
					label,
					valid: Boolean(check?.valid),
					detail: check?.valid ? detail : null,
					error: !check?.valid ? check?.error : null,
			  }))
			: [];

	return (
		<MainContainer>
			<Dialog
				open={diagnosisModalOpen}
				onClose={() => setDiagnosisModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{i18n.t(`${cs}.diagnosis.title`, { name: diagnosingConnection?.name })}
				</DialogTitle>
				<DialogContent>
					{diagnosisLoading && (
						<Box display="flex" justifyContent="center" padding={3}>
							<CircularProgress />
						</Box>
					)}
					{!diagnosisLoading && diagnosisResults && (
						<Box display="flex" flexDirection="column" style={{ gap: 16 }}>
							{diagnosisChecks.map(({ label, valid, detail, error }) => (
								<Box key={label} display="flex" alignItems="flex-start" style={{ gap: 12 }}>
									{valid ? (
										<CheckCircleOutline style={{ color: "#4caf50", marginTop: 2 }} />
									) : (
										<ErrorOutline style={{ color: "#f44336", marginTop: 2 }} />
									)}
									<Box>
										<Typography variant="subtitle2">{label}</Typography>
										{valid && detail && (
											<Typography variant="body2" color="textSecondary">
												{detail}
											</Typography>
										)}
										{!valid && error && (
											<Typography variant="body2" style={{ color: "#f44336" }}>
												{error}
											</Typography>
										)}
									</Box>
								</Box>
							))}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleRetestDiagnosingConnection}
						color="primary"
						disabled={diagnosisLoading}
					>
							{tDiagnosis("retry")}
					</Button>
					<Button onClick={() => setDiagnosisModalOpen(false)} color="primary">
							{tDiagnosis("close")}
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<Menu
				anchorEl={sessionMenuAnchorEl}
				open={Boolean(sessionMenuAnchorEl)}
				onClose={handleCloseSessionMenu}
				keepMounted
			>
				<MenuItem
					onClick={() => handleSessionMenuAction("qrcode")}
					disabled={!canShowQrAction(sessionMenuWhatsApp)}
				>
					<ListItemIcon>
						<CropFree fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.viewQr")}
						secondary={i18n.t("connections.sessionMenu.viewQrHelp")}
					/>
				</MenuItem>
				<MenuItem onClick={() => handleSessionMenuAction("restart")}>
					<ListItemIcon>
						<Refresh fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.restart")}
						secondary={i18n.t("connections.sessionMenu.restartHelp")}
					/>
				</MenuItem>
				<MenuItem onClick={() => handleSessionMenuAction("pause")}>
					<ListItemIcon>
						<PauseCircleOutline fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.pause")}
						secondary={i18n.t("connections.sessionMenu.pauseHelp")}
					/>
				</MenuItem>
				<MenuItem onClick={() => handleSessionMenuAction("syncOpenTicketsHistory")}>
					<ListItemIcon>
						<Refresh fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.syncOpenTicketsHistory")}
						secondary={i18n.t("connections.sessionMenu.syncOpenTicketsHistoryHelp")}
					/>
				</MenuItem>
				<MenuItem onClick={() => handleSessionMenuAction("newQr")}>
					<ListItemIcon>
						<CropFree fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.newQr")}
						secondary={i18n.t("connections.sessionMenu.newQrHelp")}
					/>
				</MenuItem>
				<MenuItem onClick={() => handleSessionMenuAction("disconnect")}>
					<ListItemIcon>
						<PowerSettingsNew fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={i18n.t("connections.sessionMenu.disconnect")}
						secondary={i18n.t("connections.sessionMenu.disconnectHelp")}
					/>
				</MenuItem>
			</Menu>
			<Dialog
				open={deleteModalInfo.open}
				onClose={handleCloseDeleteModal}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{i18n.t("connections.confirmationModal.deleteTitle")}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary">
						Remover a conexao "{deleteModalInfo.sourceName}".
					</Typography>
					{deleteModalInfo.requireTarget ? (
						<>
							<Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
								Esta conexao possui tickets abertos. Escolha uma conexao ativa para receber esses atendimentos antes de concluir a exclusao.
							</Typography>
							<TextField
								select
								fullWidth
								style={{ marginTop: 16 }}
								label="Conexao destino para migrar tickets abertos"
								value={deleteModalInfo.targetWhatsappId}
								onChange={e =>
									setDeleteModalInfo(prev => ({
										...prev,
										targetWhatsappId: e.target.value,
									}))
								}
								required
								helperText="Selecao obrigatoria para concluir a exclusao."
							>
								<MenuItem value="">Selecione uma conexao ativa</MenuItem>
								{getActiveMigrationTargets(
									deleteModalInfo.type === "official"
										? resolveOfficialFallbackWhatsappId(deleteModalInfo.sourceId)
										: deleteModalInfo.sourceId
								).map(option => (
									<MenuItem key={`migrate-${option.id}`} value={String(option.id)}>
										{option.name}
									</MenuItem>
								))}
							</TextField>
						</>
					) : (
						<Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
							Se nao houver tickets abertos, a conexao sera removida imediatamente. Caso existam tickets abertos, o sistema pedira a conexao destino antes de concluir.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteModal} color="default" disabled={deleteModalInfo.loading}>
						{tcs("cancel")}
					</Button>
					<Button
						onClick={handleSubmitDeleteModal}
						color="secondary"
						variant="contained"
						disabled={deleteModalInfo.loading}
					>
						{deleteModalInfo.loading ? "Excluindo..." : "Excluir conexao"}
					</Button>
				</DialogActions>
			</Dialog>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<Dialog
				open={facebookModalOpen}
				onClose={handleCloseFacebookModal}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{editingFacebookConnection ? tFacebook("titleEdit") : tFacebook("titleAdd")}
				</DialogTitle>
				<DialogContent>
					{!editingFacebookConnection?.id && !facebookSetupMode && (
						<Box className={classes.setupModeContainer} style={{ marginTop: 8 }}>
							<Paper
								variant="outlined"
								className={classes.setupOptionCard}
								onClick={() => setFacebookSetupMode("facebook_login")}
							>
								<Typography variant="h6">{tcs("connectWithMeta")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tFacebook("autoDescription")}
								</Typography>
							</Paper>
							<Paper
								variant="outlined"
								className={classes.setupOptionCard}
								onClick={handleSelectFacebookManualSetup}
							>
								<Typography variant="h6">{tcs("configureManually")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tFacebook("manualDescription")}
								</Typography>
							</Paper>
						</Box>
					)}

					{(facebookSetupMode === "manual" ||
						(editingFacebookConnection?.id && facebookSetupMode !== "facebook_login")) && (
						<Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8 }}>
							<Typography variant="body2" color="textSecondary">
								{tFacebook("manualIntro")}
							</Typography>
							<TextField
								label={tcs("connectionName")}
								variant="outlined"
								size="small"
								fullWidth
								autoComplete="off"
								value={facebookConfig.name}
								onChange={handleFacebookFieldChange("name")}
								inputProps={{ maxLength: 120 }}
								required
							/>
							<TextField
								label="Page ID"
								variant="outlined"
								size="small"
								fullWidth
								autoComplete="off"
								value={facebookConfig.pageId}
								onChange={handleFacebookFieldChange("pageId")}
								inputProps={{ maxLength: 80 }}
								helperText={tFacebook("pageIdHelp")}
								required
							/>
							<TextField
								label={editingFacebookConnection ? tFacebook("pageAccessTokenEdit") : "Page Access Token"}
								variant="outlined"
								size="small"
								type="password"
								fullWidth
								autoComplete="new-password"
								value={facebookConfig.pageAccessToken}
								onChange={handleFacebookFieldChange("pageAccessToken")}
								helperText={tFacebook("pageAccessTokenHelp")}
								required={!editingFacebookConnection}
							/>
							<TextField
								label="Verify Token"
								variant="outlined"
								size="small"
								fullWidth
								autoComplete="off"
								value={facebookConfig.verifyToken}
								onChange={handleFacebookFieldChange("verifyToken")}
								inputProps={{ maxLength: 120 }}
								helperText={tFacebook("verifyTokenHelp")}
								required
							/>
							<TextField
								select
								label={tcs("queueForNewContacts")}
								variant="outlined"
								size="small"
								fullWidth
								value={facebookConfig.queueId}
								onChange={handleFacebookFieldChange("queueId")}
								helperText={tFacebook("queueHelp")}
							>
								<MenuItem value="">{tcs("noDefaultQueue")}</MenuItem>
								{queueOptions.map(queue => (
									<MenuItem key={`facebook-queue-${queue.id}`} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label={tcs("userForNewContacts")}
								variant="outlined"
								size="small"
								fullWidth
								value={facebookConfig.defaultUserId}
								onChange={handleFacebookFieldChange("defaultUserId")}
								helperText={tcs("userForNewContactsHelp")}
							>
								<MenuItem value="">{tcs("selectUser")}</MenuItem>
								{userOptions.map(user => (
									<MenuItem key={`facebook-user-${user.id}`} value={String(user.id)}>
										{user.name}
									</MenuItem>
								))}
							</TextField>
							<FormControlLabel
								control={
									<Switch
										checked={Boolean(facebookConfig.isDefault)}
										onChange={handleFacebookFieldChange("isDefault")}
										color="primary"
									/>
								}
								label={tcs("defaultConnection")}
							/>
							<Paper variant="outlined" style={{ padding: 12 }}>
								<Typography variant="subtitle2" gutterBottom>
									{tFacebook("webhookTitle")}
								</Typography>
								<Typography variant="body2" color="textSecondary">
									{tFacebook("webhookInstructions")}
								</Typography>
								<Typography variant="body2" className={classes.webhookText} style={{ marginTop: 8 }}>
									{editingFacebookConnection?.webhookUrl || "/webhook/facebook-messenger"}
								</Typography>
							</Paper>
						</Box>
					)}

					{facebookSetupMode === "facebook_login" && (
						<Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8 }}>
							<Typography variant="body2" color="textSecondary">
								{tFacebook("autoIntro")}
							</Typography>
							<TextField
								label={tcs("connectionName")}
								variant="outlined"
								size="small"
								fullWidth
								autoComplete="off"
								value={facebookConfig.name}
								onChange={handleFacebookFieldChange("name")}
								inputProps={{ maxLength: 120 }}
								helperText={tFacebook("nameAutoHelp")}
							/>
							<TextField
								select
								label={tcs("queueForNewContacts")}
								variant="outlined"
								size="small"
								fullWidth
								value={facebookConfig.queueId}
								onChange={handleFacebookFieldChange("queueId")}
							>
								<MenuItem value="">{tcs("noDefaultQueue")}</MenuItem>
								{queueOptions.map(queue => (
									<MenuItem key={`facebook-auto-queue-${queue.id}`} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label={tcs("userForNewContacts")}
								variant="outlined"
								size="small"
								fullWidth
								value={facebookConfig.defaultUserId}
								onChange={handleFacebookFieldChange("defaultUserId")}
								helperText={tcs("userForNewContactsHelp")}
							>
								<MenuItem value="">{tcs("selectUser")}</MenuItem>
								{userOptions.map(user => (
									<MenuItem key={`facebook-auto-user-${user.id}`} value={String(user.id)}>
										{user.name}
									</MenuItem>
								))}
							</TextField>
							<FormControlLabel
								control={
									<Switch
										checked={Boolean(facebookConfig.isDefault)}
										onChange={handleFacebookFieldChange("isDefault")}
										color="primary"
									/>
								}
								label={tcs("defaultConnection")}
							/>
							<Button
								color="primary"
								variant="contained"
								onClick={handleStartFacebookMetaSetup}
								disabled={facebookOAuthLoading}
							>
								{facebookOAuthLoading && !facebookOAuthPages.length ? tcs("connecting") : tcs("connectWithMeta")}
							</Button>
							{facebookOAuthPages.length > 0 && (
								<Paper variant="outlined" style={{ padding: 12 }}>
									<Typography variant="subtitle2" gutterBottom>
										{tFacebook("choosePageTitle")}
									</Typography>
									<Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
										{tFacebook("choosePageHelp")}
									</Typography>
									<TextField
										select
										fullWidth
										variant="outlined"
										size="small"
										label={tFacebook("pageLabel")}
										value={facebookSelectedPageId}
										onChange={e => setFacebookSelectedPageId(String(getSafeEventValue(e) || ""))}
									>
										{facebookOAuthPages.map(page => (
											<MenuItem key={`facebook-page-${page.id}`} value={String(page.id)}>
												{page.name}
												{page.category ? ` - ${page.category}` : ""}
											</MenuItem>
										))}
									</TextField>
								</Paper>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{facebookSetupMode && (
						<Button onClick={handleBackToFacebookSetupOptions} color="default" disabled={facebookSaving || facebookOAuthLoading}>
							{tcs("back")}
						</Button>
					)}
					<Button onClick={handleCloseFacebookModal} color="default" disabled={facebookSaving || facebookOAuthLoading}>
						{tcs("cancel")}
					</Button>
					{(facebookSetupMode === "manual" ||
						(editingFacebookConnection?.id && facebookSetupMode !== "facebook_login")) && (
						<Button
							onClick={handleSaveFacebookConnection}
							color="primary"
							disabled={facebookSaving}
						>
							{facebookSaving ? tcs("saving") : tcs("save")}
						</Button>
					)}
					{facebookSetupMode === "facebook_login" && facebookOAuthPages.length > 0 && (
						<Button
							onClick={handleCompleteFacebookMetaSetup}
							color="primary"
							variant="contained"
							disabled={facebookOAuthLoading || !facebookSelectedPageId}
						>
								{facebookOAuthLoading ? tcs("saving") : tFacebook("complete")}
						</Button>
					)}
				</DialogActions>
			</Dialog>
			<Dialog
				open={webchatModalOpen}
				onClose={handleCloseWebchatModal}
				maxWidth={webchatModalTab === "script" ? "md" : "sm"}
				fullWidth
			>
				<DialogTitle>
					{editingWebchatConnection ? "Editar Conexao Webchat" : "Nova Conexao Webchat"}
				</DialogTitle>
				<DialogContent>
					<Tabs
						value={webchatModalTab}
						onChange={(_event, value) => setWebchatModalTab(value)}
						indicatorColor="primary"
						textColor="primary"
						variant="scrollable"
						scrollButtons="auto"
						style={{ marginTop: 4 }}
					>
						<Tab value="settings" label="Configuracoes" />
						<Tab
							value="script"
							label="Script e personalizacao"
							disabled={!editingWebchatConnection?.id}
						/>
					</Tabs>

					{webchatModalTab === "settings" && (
						<Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 12 }}>
							<TextField
								label={tcs("connectionName")}
								variant="outlined"
								size="small"
								value={String(webchatConfig.name || "")}
								onChange={e => {
									const name = String(getSafeEventValue(e) || "");
									setWebchatConfig(prev => ({
										...prev,
										name,
									}));
								}}
							/>
							<TextField
								select
								label={tcs("queueForNewContacts")}
								variant="outlined"
								size="small"
								value={String(webchatConfig.queueId || "")}
								onChange={e => {
									const queueId = String(getSafeEventValue(e) || "");
									setWebchatConfig(prev => ({
										...prev,
										queueId,
									}));
								}}
							>
								<MenuItem value="">{tcs("selectQueue")}</MenuItem>
								{queueOptions.map(queue => (
									<MenuItem key={`webchat-queue-${queue.id}`} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label={tcs("userForNewContacts")}
								variant="outlined"
								size="small"
								fullWidth
								value={String(webchatConfig.defaultUserId || "")}
								onChange={e => {
									const defaultUserId = String(getSafeEventValue(e) || "");
									setWebchatConfig(prev => ({
										...prev,
										defaultUserId,
									}));
								}}
								helperText={tcs("userForNewContactsHelp")}
							>
								<MenuItem value="">{tcs("selectUser")}</MenuItem>
								{userOptions.map(user => (
									<MenuItem key={`webchat-user-${user.id}`} value={String(user.id)}>
										{user.name}
									</MenuItem>
								))}
							</TextField>
							{!editingWebchatConnection?.id && (
								<Typography variant="caption" color="textSecondary">
									Salve primeiro a conexao para liberar a aba de Script e personalizacao.
								</Typography>
							)}
						</Box>
					)}

					{webchatModalTab === "script" && (
						<>
							{webchatScriptLoading && (
								<Box display="flex" justifyContent="center" padding={3}>
									<CircularProgress />
								</Box>
							)}
							{!webchatScriptLoading && webchatScriptPayload && (
								<Box display="flex" flexDirection="column" style={{ gap: 14, marginTop: 12 }}>
									<Typography variant="body2" color="textSecondary">
										Conexao: {webchatScriptPayload.connectionName}
									</Typography>
									<Paper variant="outlined" style={{ padding: 12 }}>
										<Typography variant="subtitle2" gutterBottom>
											Como aplicar no seu website
										</Typography>
										<Typography variant="body2" color="textSecondary">
											1. Escolha uma aba de layout abaixo (Botao flutuante, Embed inline ou Painel lateral).
										</Typography>
										<Typography variant="body2" color="textSecondary">
											2. Clique em Copiar e cole o snippet completo no HTML do seu site.
										</Typography>
										<Typography variant="body2" color="textSecondary">
											3. Publique o site e teste abrindo a pagina em aba anonima para evitar cache.
										</Typography>
										<Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
											Dica: se voce usa WordPress, cole no rodape do tema (footer) ou em plugin de insercao de scripts. Se usa GTM, dispare em todas as paginas.
										</Typography>
									</Paper>
									<Paper variant="outlined" style={{ padding: 12 }}>
										<Typography variant="subtitle2" gutterBottom>
											Personalizacao visual (pratica)
										</Typography>
										<Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
											Escolha o jeito mais simples para personalizar: preset de cor, ajuste de tonalidade, ou cor HEX. Tambem e possivel definir cantos quadrados ou arredondados.
										</Typography>
										<Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
											{tWebchat("customizationSaveHelp")}
										</Typography>
										<Box display="grid" style={{ gap: 10 }}>
											<TextField
												select
												label="Modo de cor"
												variant="outlined"
												size="small"
												value={scriptColorMode}
												onChange={e => setScriptColorMode(String(getSafeEventValue(e) || "preset"))}
											>
												<MenuItem value="preset">Preset + tonalidade</MenuItem>
												<MenuItem value="hex">Cor HEX</MenuItem>
												<MenuItem value="neutral">Sem cor (neutro)</MenuItem>
											</TextField>
											{scriptColorMode === "preset" && (
												<TextField
													select
													label="Cor pre-definida"
													variant="outlined"
													size="small"
													value={scriptColorPreset}
													onChange={e => setScriptColorPreset(String(getSafeEventValue(e) || "flowch"))}
												>
													{SCRIPT_COLOR_PRESETS.map(item => (
														<MenuItem key={`script-preset-${item.value}`} value={item.value}>
															{item.label} ({item.hex})
														</MenuItem>
													))}
												</TextField>
											)}
											{scriptColorMode === "preset" && (
												<Box>
													<Typography variant="caption" color="textSecondary">
														Tonalidade: {scriptHueShift > 0 ? `+${scriptHueShift}` : scriptHueShift}
													</Typography>
													<Slider
														value={scriptHueShift}
														onChange={(_event, value) => setScriptHueShift(Number(value || 0))}
														step={1}
														min={-180}
														max={180}
														valueLabelDisplay="auto"
													/>
												</Box>
											)}
											{scriptColorMode === "hex" && (
												<TextField
													label="Cor HEX"
													variant="outlined"
													size="small"
													helperText="Ex.: #0c8c63"
													value={scriptHexColor}
													onChange={e => setScriptHexColor(String(getSafeEventValue(e) || ""))}
												/>
											)}
											<TextField
												select
												label="Cantos"
												variant="outlined"
												size="small"
												value={scriptCornerStyle}
												onChange={e => setScriptCornerStyle(String(getSafeEventValue(e) || "rounded"))}
											>
												<MenuItem value="rounded">Arredondados</MenuItem>
												<MenuItem value="square">Quadrados</MenuItem>
											</TextField>
											<TextField
												select
												label="Animacao do botao flutuante"
												variant="outlined"
												size="small"
												value={scriptButtonAnimation}
												onChange={e => setScriptButtonAnimation(String(getSafeEventValue(e) || "pulse"))}
												helperText="Afeta apenas o layout Botao flutuante."
											>
												<MenuItem value="pulse">Pulso sutil</MenuItem>
												<MenuItem value="bounce">Pulo leve</MenuItem>
												<MenuItem value="none">Sem animacao</MenuItem>
											</TextField>
											<Box display="flex" flexDirection="column" style={{ gap: 4 }}>
												<FormControlLabel
													control={
														<Switch
															checked={scriptFrame}
															onChange={event => setScriptFrame(event.target.checked)}
															color="primary"
														/>
													}
													label={tWebchat("showFrame")}
												/>
												<Typography variant="body2" color="textSecondary">
													{tWebchat("showFrameHelp")}
												</Typography>
											</Box>
											<Box
												display="flex"
												flexDirection="column"
												style={{
													gap: 8,
													padding: 12,
													border: "1px solid rgba(0,0,0,0.12)",
													borderRadius: 8,
												}}
											>
												<Typography variant="subtitle2">
													{tWebchat("prechatTitle")}
												</Typography>
												<Typography variant="body2" color="textSecondary">
													{tWebchat("prechatHelp")}
												</Typography>
												<Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
													<FormControlLabel
														control={
															<Switch
																checked={scriptAskName}
																onChange={event => setScriptAskName(event.target.checked)}
																color="primary"
															/>
														}
														label={tWebchat("askName")}
													/>
													<FormControlLabel
														control={
															<Switch
																checked={scriptAskEmail}
																onChange={event => setScriptAskEmail(event.target.checked)}
																color="primary"
															/>
														}
														label={tWebchat("askEmail")}
													/>
													<FormControlLabel
														control={
															<Switch
																checked={scriptAskWhatsapp}
																onChange={event => setScriptAskWhatsapp(event.target.checked)}
																color="primary"
															/>
														}
														label={tWebchat("askWhatsapp")}
													/>
												</Box>
											</Box>
											<Box display="flex" alignItems="center" style={{ gap: 8 }}>
												<Typography variant="caption" color="textSecondary">
													Cor principal aplicada:
												</Typography>
												<Box
													style={{
														width: 24,
														height: 24,
														borderRadius: 6,
														border: "1px solid rgba(0,0,0,0.18)",
														backgroundColor: primaryThemeColor,
													}}
												/>
												<Typography variant="caption">{primaryThemeColor}</Typography>
											</Box>
										</Box>
									</Paper>
									<Tabs
										value={selectedScriptTab}
										onChange={(_event, value) => setSelectedScriptTab(value)}
										indicatorColor="primary"
										textColor="primary"
										variant="scrollable"
										scrollButtons="auto"
									>
										<Tab value="floating" label="Botao flutuante" />
										<Tab value="inline" label="Embed inline" />
										<Tab value="panel" label="Painel lateral" />
									</Tabs>
									<Box>
										<Box display="flex" justifyContent="space-between" alignItems="center">
											<Typography variant="subtitle2">
												{selectedScriptLabel}
											</Typography>
											<Button
												size="small"
												startIcon={<FileCopy fontSize="small" />}
												onClick={() => handleCopySnippet(selectedSnippet)}
											>
												Copiar
											</Button>
										</Box>
										<Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
											Onde colar: {selectedScriptPlacement}
										</Typography>
										<TextField
											fullWidth
											multiline
											minRows={4}
											variant="outlined"
											value={selectedSnippet}
										/>
										<Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
											Checklist rapido: script carregou sem erro no console, widget apareceu na pagina e envio de mensagem chegou no atendimento.
										</Typography>
									</Box>
									<Paper variant="outlined" style={{ padding: 12 }}>
										<Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12 }}>
											<Typography variant="subtitle2">
												Teste avancado: mais de um layout na mesma pagina
											</Typography>
											<Button
												size="small"
												startIcon={<FileCopy fontSize="small" />}
												onClick={() => handleCopySnippet(multiLayoutSnippet)}
											>
												Copiar exemplo
											</Button>
										</Box>
										<Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
											Use somente para testes visuais. Regra obrigatoria: carregar o widget.js uma unica vez e chamar os layouts com multiplos init no mesmo script.
										</Typography>
										<Typography variant="body2" color="textSecondary">
											Se repetir o script src do widget na mesma pagina, pode quebrar o carregamento.
										</Typography>
										<TextField
											fullWidth
											multiline
											minRows={8}
											variant="outlined"
											style={{ marginTop: 10 }}
											value={multiLayoutSnippet}
										/>
									</Paper>
								</Box>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					{webchatModalTab === "settings" ? (
						<>
							<Button onClick={handleCloseWebchatModal} color="default">
								{tcs("cancel")}
							</Button>
							{editingWebchatConnection?.id && (
								<Button onClick={() => setWebchatModalTab("script")} color="default">
									Abrir Script e personalizacao
								</Button>
							)}
							<Button onClick={handleSaveWebchatConnection} color="primary" variant="contained">
								{tcs("save")}
							</Button>
						</>
					) : (
						<>
							<Button onClick={() => setWebchatModalTab("settings")} color="default">
								Voltar para configuracoes
							</Button>
							<Button
								onClick={persistWebchatAppearanceNow}
								color="primary"
								variant="contained"
								disabled={webchatAppearanceSaving || !webchatAppearanceDirty}
							>
								{webchatAppearanceSaving ? tcs("saving") : tWebchat("saveCustomization")}
							</Button>
							<Button onClick={handleCloseWebchatModal} color="primary">
								Fechar
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
			<Dialog
				open={apiModalOpen}
				onClose={handleCloseApiModal}
				maxWidth="sm"
				fullWidth
			>
					<DialogTitle>
						{editingOfficialConnection
							? tOfficial("titleEdit")
							: tOfficial("titleAdd")}
				</DialogTitle>
				<DialogContent>
					{editingOfficialConnection?.id && (
						<Tabs
							value={apiModalTab}
							onChange={(_event, value) => setApiModalTab(value)}
							indicatorColor="primary"
							textColor="primary"
							variant="scrollable"
							scrollButtons="auto"
							style={{ marginTop: 4, marginBottom: 12 }}
						>
							<Tab value="settings" label="Configuracoes" />
							<Tab value="templates" label="Templates" />
						</Tabs>
					)}

					{apiModalTab === "templates" && editingOfficialConnection?.id && (
						<Box display="flex" flexDirection="column" style={{ gap: 14 }}>
							<Paper variant="outlined" style={{ padding: 12 }}>
								<Typography variant="subtitle2" gutterBottom>
									{tOfficial("templates.createTitle")}
								</Typography>
								<Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
									{tOfficial("templates.createHelp")}
								</Typography>
								<Box display="grid" style={{ gap: 10 }}>
									<TextField
										fullWidth
										label={tOfficial("templates.technicalName")}
										variant="outlined"
										size="small"
										helperText={tOfficial("templates.technicalNameHelp")}
										value={newTemplateForm.name}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, name: nextValue }));
										}}
									/>
									<TextField
										fullWidth
										label={tOfficial("templates.friendlyName")}
										variant="outlined"
										size="small"
										value={newTemplateForm.friendlyName}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, friendlyName: nextValue }));
										}}
									/>
									<TextField
										select
										fullWidth
										label={tOfficial("templates.category")}
										variant="outlined"
										size="small"
										value={newTemplateForm.category}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, category: nextValue }));
										}}
									>
										<MenuItem value="UTILITY">UTILITY</MenuItem>
										<MenuItem value="MARKETING">MARKETING</MenuItem>
										<MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
									</TextField>
									<TextField
										fullWidth
										label={tOfficial("templates.language")}
										variant="outlined"
										size="small"
										value={newTemplateForm.language}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, language: nextValue }));
										}}
									/>
									<TextField
										select
										fullWidth
										label={tOfficial("templates.parameterFormat")}
										variant="outlined"
										size="small"
										helperText={
											newTemplateForm.parameterFormat === "NAMED"
												? tOfficial("templates.namedHelp")
												: tOfficial("templates.positionalHelp")
										}
										value={newTemplateForm.parameterFormat}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, parameterFormat: nextValue }));
										}}
									>
										<MenuItem value="NAMED">{tOfficial("templates.namedFormat")}</MenuItem>
										<MenuItem value="POSITIONAL">{tOfficial("templates.positionalFormat")}</MenuItem>
									</TextField>
									{newTemplateForm.parameterFormat === "NAMED" && (
										<Box>
											<Typography variant="caption" color="textSecondary">
												{tOfficial("templates.insertVariable")}
											</Typography>
											<Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 6 }}>
												{TEMPLATE_VARIABLE_PRESETS.map(variable => (
													<Button
														key={variable}
														size="small"
														variant="outlined"
														onClick={() => setNewTemplateForm(prev => ({
															...prev,
															bodyText: `${prev.bodyText}${prev.bodyText && !/\s$/.test(prev.bodyText) ? " " : ""}{{${variable}}}`,
														}))}
													>
														{`{{${variable}}}`}
													</Button>
												))}
											</Box>
										</Box>
									)}
									<TextField
										fullWidth
										label={tOfficial("templates.body")}
										variant="outlined"
										size="small"
										multiline
										minRows={3}
										helperText={
											newTemplateForm.parameterFormat === "NAMED"
												? tOfficial("templates.bodyNamedHelp")
												: tOfficial("templates.bodyPositionalHelp")
										}
										value={newTemplateForm.bodyText}
										onChange={e => {
											const nextValue = getSafeEventValue(e);
											setNewTemplateForm(prev => ({ ...prev, bodyText: nextValue }));
										}}
									/>
									{newTemplateForm.parameterFormat === "NAMED" && newTemplateNamedVariables.map(variable => (
										<TextField
											key={variable}
											fullWidth
											label={tOfficial("templates.exampleFor", { variable })}
											variant="outlined"
											size="small"
											required
											value={newTemplateForm.parameterExamples?.[variable] || ""}
											onChange={e => {
												const nextValue = getSafeEventValue(e);
												setNewTemplateForm(prev => ({
													...prev,
													parameterExamples: { ...prev.parameterExamples, [variable]: nextValue },
												}));
											}}
										/>
									))}
									{newTemplateForm.parameterFormat === "POSITIONAL" && newTemplatePositionalVariables.map(variable => {
										const exampleIndex = Number(variable) - 1;
										return (
											<TextField
												key={variable}
												fullWidth
												label={tOfficial("templates.exampleFor", { variable })}
												variant="outlined"
												size="small"
												required
												value={newTemplateForm.positionalExamples?.[exampleIndex] || ""}
												onChange={e => {
													const nextValue = getSafeEventValue(e);
													setNewTemplateForm(prev => {
														const nextExamples = [...(prev.positionalExamples || [])];
														nextExamples[exampleIndex] = nextValue;
														return { ...prev, positionalExamples: nextExamples };
													});
												}}
											/>
										);
									})}
								</Box>
								<Box display="flex" justifyContent="flex-end" style={{ marginTop: 12 }}>
									<Button
										onClick={handleCreateTemplate}
										color="primary"
										variant="contained"
										disabled={officialTemplatesSaving}
									>
										{tOfficial("templates.createAction")}
									</Button>
								</Box>
							</Paper>

							<Paper variant="outlined" style={{ padding: 12 }}>
								<Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, marginBottom: 10 }}>
									<Typography variant="subtitle2">Templates aprovados da conexao</Typography>
									<Button
										onClick={handleSyncOfficialTemplates}
										color="primary"
										disabled={officialTemplatesLoading}
									>
										Sincronizar
									</Button>
								</Box>
								<Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
									A lista abaixo representa somente templates da conexao atual na Meta. Defina um nome amigavel para facilitar a operacao do time.
								</Typography>
								{officialTemplatesMeta?.wabaId && (
									<Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
										Fonte dos templates: WABA {officialTemplatesMeta.wabaId}
										{officialTemplatesMeta.configuredWabaId && officialTemplatesMeta.configuredWabaId !== officialTemplatesMeta.wabaId
											? ` (configurada na conexao: ${officialTemplatesMeta.configuredWabaId})`
											: ""}
									</Typography>
								)}

								{officialTemplatesLoading ? (
									<Box display="flex" justifyContent="center" padding={2}>
										<CircularProgress size={22} />
									</Box>
								) : officialTemplateRows.length === 0 ? (
									<Typography variant="body2" color="textSecondary">
										Nenhum template aprovado encontrado para esta conexao.
									</Typography>
								) : (
									<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
										{officialTemplateRows.map(row => {
											const aliasKey = row.aliasKey;
											return (
												<Paper key={row.rowId} variant="outlined" style={{ padding: 10 }}>
													<Typography variant="subtitle2">
														{row.name} ({row.language})
													</Typography>
													<Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
														Status: {row.status} | Categoria: {row.category}
													</Typography>
													<TextField
														fullWidth
														label="Nome amigavel"
														variant="outlined"
														size="small"
														id={`template-alias-${row.rowId}`}
														autoComplete="off"
														defaultValue={templateAliasesDraft[aliasKey] || ""}
														onChange={e => {
															const nextValue = getSafeEventValue(e);
															templateAliasesDraftRef.current = {
																...templateAliasesDraftRef.current,
																[aliasKey]: nextValue,
															};
														}}
														onBlur={e => {
															const nextValue = getSafeEventValue(e);
															setTemplateAliasesDraft(prev => ({
																...prev,
																[aliasKey]: nextValue,
															}));
														}}
													/>
												</Paper>
											);
										})}
										<Box display="flex" justifyContent="flex-end">
											<Button
												onClick={handleSaveTemplateAliases}
												color="primary"
												variant="contained"
												disabled={officialTemplatesSaving}
											>
												Salvar nomes amigaveis
											</Button>
										</Box>
									</Box>
								)}
							</Paper>

								<Paper variant="outlined" style={{ padding: 12 }}>
									<Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, marginBottom: 10 }}>
										<Typography variant="subtitle2">Templates em analise / nao aprovados</Typography>
									</Box>
									<Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
										Use esta lista para acompanhar o status da aprovacao na Meta antes de liberar o uso operacional.
									</Typography>

									{officialTemplatesLoading ? (
										<Box display="flex" justifyContent="center" padding={2}>
											<CircularProgress size={22} />
										</Box>
									) : officialTemplateReviewRows.length === 0 ? (
										<Typography variant="body2" color="textSecondary">
											Nenhum template pendente ou nao aprovado encontrado para esta conexao.
										</Typography>
									) : (
										<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
											{officialTemplateReviewRows.map(row => (
												<Paper key={`review-${row.rowId}`} variant="outlined" style={{ padding: 10 }}>
													<Typography variant="subtitle2">
														{row.name} ({row.language})
													</Typography>
													<Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
														Status atual na Meta: {row.status}
													</Typography>
													<Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
														Categoria: {row.category}
													</Typography>
												</Paper>
											))}
										</Box>
									)}
								</Paper>
						</Box>
					)}

					{apiModalTab !== "templates" && (
						<>
					{!apiSetupMode && (
						<div className={classes.setupModeContainer}>
							<Paper
								className={classes.setupOptionCard}
								onClick={handleSelectMetaSetup}
							>
								<Typography variant="h6">{tcs("connectWithMeta")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("autoDescription")}
								</Typography>
							</Paper>
							<Paper
								className={classes.setupOptionCard}
								onClick={handleSelectManualSetup}
							>
								<Typography variant="h6">{tcs("configureManually")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("manualDescription")}
								</Typography>
							</Paper>
						</div>
					)}

					{apiSetupMode === "manual_choice" && (
						<div className={classes.setupModeContainer}>
							<Paper
								className={classes.setupOptionCard}
								onClick={() => setApiSetupMode("manual")}
							>
								<Typography variant="h6">{tcs("officialApi")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("officialApiDescription")}
								</Typography>
							</Paper>
							<Paper
								className={classes.setupOptionCard}
								onClick={handleSelectManualQrCodeConnection}
							>
								<Typography variant="h6">{tcs("qrCode")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("qrCodeDescription")}
								</Typography>
							</Paper>
						</div>
					)}

					{apiSetupMode === "meta_mode_choice" && (
						<div className={classes.setupModeContainer}>
							<Paper
								className={classes.setupOptionCard}
								onClick={() => handleSelectMetaOnboardingMode("cloud_api")}
							>
								<Typography variant="h6">{tOfficial("cloudApiModeTitle")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("cloudApiModeDescription")}
								</Typography>
							</Paper>
							<Paper
								className={classes.setupOptionCard}
								style={{
									opacity: officialMetaOptions?.coexistence?.available ? 1 : 0.62,
								}}
								onClick={() => handleSelectMetaOnboardingMode("coexistence")}
							>
								<Typography variant="h6">{tOfficial("coexistenceModeTitle")}</Typography>
								<Typography variant="body2" color="textSecondary">
									{tOfficial("coexistenceModeDescription")}
								</Typography>
								{!officialMetaOptions?.coexistence?.available && (
									<Typography variant="caption" color="error">
										{tOfficial("coexistenceConfigurationPending")}
									</Typography>
								)}
							</Paper>
						</div>
					)}

					{(apiSetupMode === "manual" || apiSetupMode === "meta_embedded_signup") && (
						<div className={classes.metaFlowBox}>
							<TextField
								select
								fullWidth
								InputLabelProps={{ style: { whiteSpace: "nowrap" } }}
								label={tOfficial("metaApplicationLabel")}
								variant="outlined"
								size="small"
								value={selectedMetaApplicationId}
								onChange={e => setSelectedMetaApplicationId(e.target.value)}
								helperText={tOfficial("metaApplicationHelp")}
							>
								<MenuItem value="">{tOfficial("metaApplicationFlowchDefault")}</MenuItem>
								{metaApplications
									.filter(application => application.status === "active")
									.map(application => (
										<MenuItem key={application.id} value={String(application.id)}>
											{application.name} · {application.appId} · {application.ownerType === "flowch"
												? tOfficial("metaApplicationOwnerFlowch")
												: tOfficial("metaApplicationOwnerCompany")}
										</MenuItem>
									))}
							</TextField>
							<Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
								<Button variant="outlined" onClick={handleNewMetaApplication}>
									{tOfficial("metaApplicationAdd")}
								</Button>
								{selectedMetaApplicationId &&
									metaApplications.find(
										application => String(application.id) === selectedMetaApplicationId
									)?.ownerType === "company" && (
										<Button
											variant="outlined"
											onClick={() =>
												handleEditMetaApplication(
													metaApplications.find(
														application => String(application.id) === selectedMetaApplicationId
													)
												)
											}
										>
											{tOfficial("metaApplicationEdit")}
										</Button>
									)}
							</Box>
							{metaApplicationFormOpen && (
								<Paper variant="outlined" style={{ padding: 12 }}>
									<Typography variant="subtitle2">
										{editingMetaApplicationId
											? tOfficial("metaApplicationEditTitle")
											: tOfficial("metaApplicationAddTitle")}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										{tOfficial("metaApplicationInstructions")}
									</Typography>
									<Box display="flex" flexDirection="column" mt={1} style={{ gap: 10 }}>
										<TextField fullWidth autoComplete="off" label={tOfficial("metaApplicationName")} variant="outlined" size="small" value={metaApplicationForm.name} onChange={e => handleMetaApplicationFieldChange("name", e.target.value)} />
										<TextField fullWidth autoComplete="off" label="App ID" variant="outlined" size="small" value={metaApplicationForm.appId} onChange={e => handleMetaApplicationFieldChange("appId", e.target.value)} />
										<TextField fullWidth autoComplete="new-password" label="App Secret" variant="outlined" size="small" type="password" value={metaApplicationForm.appSecret} helperText={editingMetaApplicationId ? tOfficial("secretKeepCurrent") : tOfficial("metaApplicationSecretHelp")} onChange={e => handleMetaApplicationFieldChange("appSecret", e.target.value)} />
										<TextField fullWidth autoComplete="new-password" label="Verify Token" variant="outlined" size="small" type="password" value={metaApplicationForm.verifyToken} helperText={tOfficial(editingMetaApplicationId ? "metaApplicationVerifyTokenEditHelp" : "metaApplicationVerifyTokenHelp")} onChange={e => handleMetaApplicationFieldChange("verifyToken", e.target.value)} />
										<TextField fullWidth helperText={tOfficial("metaApplicationConfigHelp")} autoComplete="off" label="Embedded Signup Config ID" variant="outlined" size="small" value={metaApplicationForm.embeddedSignupConfigId} onChange={e => handleMetaApplicationFieldChange("embeddedSignupConfigId", e.target.value)} />
										<TextField fullWidth autoComplete="off" label="Coexistence Config ID" variant="outlined" size="small" value={metaApplicationForm.coexistenceConfigId} onChange={e => handleMetaApplicationFieldChange("coexistenceConfigId", e.target.value)} />
										<Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
											<Button color="primary" variant="contained" disableElevation onClick={handleSaveMetaApplication} disabled={metaApplicationSaving}>
												{tOfficial("metaApplicationSave")}
											</Button>
											<Button onClick={() => setMetaApplicationFormOpen(false)} disabled={metaApplicationSaving}>
												{tcs("cancel")}
											</Button>
											{editingMetaApplicationId && (
												<Button color="secondary" onClick={handleDeleteMetaApplication} disabled={metaApplicationSaving}>
													{tOfficial("metaApplicationDelete")}
												</Button>
											)}
										</Box>
										{editingMetaApplicationId && (() => {
											const application = metaApplications.find(item => item.id === editingMetaApplicationId);
											return application?.webhookPath ? (
												<Typography variant="caption" className={classes.webhookText}>
													{tOfficial("metaApplicationWebhookHelp")}: {`${window.location.origin}${application.webhookPath}`}
												</Typography>
											) : null;
										})()}
									</Box>
								</Paper>
							)}

							{selectedMetaApplicationId && (() => {
								const application = metaApplications.find(item => String(item.id) === selectedMetaApplicationId);
								return application?.webhookPath ? <Box>
									<Typography variant="body2">{tOfficial("metaApplicationWebhookHelp")}</Typography>
									<Typography variant="body2" className={classes.webhookText}>{window.location.origin}{application.webhookPath}</Typography>
									<Typography variant="caption" color="textSecondary">{tOfficial("metaApplicationWebhookChecklist")}</Typography>
								</Box> : null;
							})()}
							{queueOptions.length === 0 && <Typography role="alert" variant="body2" color="error">{tOfficial("setupQueueMissing")}</Typography>}
						</div>
					)}
					{apiSetupMode === "manual" && (
						<div className={classes.manualForm}>
							<Typography variant="body2" color="textSecondary">
								{tOfficial("manualIntro")}
								<br />{tOfficial("manualSaveEffects")}
							</Typography>
							<TextField
								fullWidth
								label={tcs("connectionName")}
								variant="outlined"
								size="small"
								value={manualConfig.name}
								onChange={e => handleManualFieldChange("name", e.target.value)}
							/>
							<TextField
								fullWidth
								label="Phone Number ID"
								variant="outlined"
								size="small"
								value={manualConfig.phoneNumberId}
								onChange={e =>
									handleManualFieldChange("phoneNumberId", e.target.value)
								}
							/>
							<TextField
								fullWidth
								label="WABA ID"
								variant="outlined"
								size="small"
								value={manualConfig.wabaId}
								onChange={e => handleManualFieldChange("wabaId", e.target.value)}
							/>
							<TextField
								fullWidth
								label="Access Token"
								variant="outlined"
								size="small"
								multiline
								rows={3}
								value={manualConfig.accessToken}
								helperText={editingOfficialConnection?.accessTokenConfigured ? tOfficial("secretKeepCurrent") : undefined}
								onChange={e =>
									handleManualFieldChange("accessToken", e.target.value)
								}
							/>
							<TextField
								fullWidth
								type="password"
								autoComplete="new-password"
								label="Verify Token"
								variant="outlined"
								size="small"
								value={manualConfig.verifyToken}
								helperText={editingOfficialConnection?.verifyTokenConfigured ? tOfficial("secretKeepCurrent") : tOfficial("manualVerifyTokenHelp")}
								onChange={e =>
									handleManualFieldChange("verifyToken", e.target.value)
								}
							/>
							<TextField
								fullWidth
								select
									label={tcs("queueForNewContacts")}
								variant="outlined"
								size="small"
								value={manualConfig.queueId}
								onChange={e => handleManualFieldChange("queueId", e.target.value)}
							>
									<MenuItem value="">{tcs("selectQueue")}</MenuItem>
								{queueOptions.map(queue => (
									<MenuItem key={queue.id} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								select
								label={tcs("outboundQueues")}
								variant="outlined"
								size="small"
								value={manualConfig.outboundQueueIds}
								onChange={e => handleManualFieldChange("outboundQueueIds", e.target.value)}
								helperText={tcs("outboundQueuesHelp")}
								SelectProps={{ multiple: true }}
							>
								{queueOptions.map(queue => (
									<MenuItem key={`outbound-${queue.id}`} value={queue.id}>{queue.name}</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								select
								label={tcs("userForNewContacts")}
								variant="outlined"
								size="small"
								value={manualConfig.defaultUserId}
								onChange={e =>
									handleManualFieldChange("defaultUserId", e.target.value)
								}
							>
								<MenuItem value="">{tcs("selectUser")}</MenuItem>
								{userOptions.map(user => (
									<MenuItem key={user.id} value={String(user.id)}>
										{user.name}
									</MenuItem>
								))}
							</TextField>
								<FormControlLabel
									control={
										<Switch
											color="primary"
											checked={Boolean(manualConfig.isDefault)}
											onChange={e =>
												handleManualFieldChange("isDefault", e.target.checked)
											}
										/>
									}
										label={tcs("makeDefaultConnection")}
								/>

							{editingOfficialConnection?.webhookUrl && (
								<Box>
									<Typography variant="caption" color="textSecondary">
										Webhook URL
									</Typography>
									<Typography className={classes.webhookText} variant="body2">
										{editingOfficialConnection.webhookUrl}
									</Typography>
								</Box>
							)}
						</div>
					)}

					{apiSetupMode === "meta_embedded_signup" && (
						<div className={classes.metaFlowBox}>
							<Typography variant="body2" color="textSecondary">
								{tOfficial(
									officialOnboardingMode === "coexistence"
										? "coexistenceIntro"
										: "metaIntro"
								)}
							</Typography>
							{officialOnboardingMode === "coexistence" && (
								<Typography variant="caption" color="textSecondary">
									{tOfficial("coexistenceEligibilityHelp")}
								</Typography>
							)}
							<TextField
								select
								label={tcs("queueForNewContacts")}
								variant="outlined"
								size="small"
								value={manualConfig.queueId}
								onChange={e => handleManualFieldChange("queueId", e.target.value)}
							>
								<MenuItem value="">{tcs("selectQueue")}</MenuItem>
								{queueOptions.map(queue => (
									<MenuItem key={queue.id} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label={tcs("outboundQueues")}
								variant="outlined"
								size="small"
								value={manualConfig.outboundQueueIds}
								onChange={e => handleManualFieldChange("outboundQueueIds", e.target.value)}
								helperText={tcs("outboundQueuesHelp")}
								SelectProps={{ multiple: true }}
							>
								{queueOptions.map(queue => (
									<MenuItem key={`meta-outbound-${queue.id}`} value={queue.id}>{queue.name}</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label={tcs("userForNewContacts")}
								variant="outlined"
								size="small"
								value={manualConfig.defaultUserId}
								onChange={e =>
									handleManualFieldChange("defaultUserId", e.target.value)
								}
							>
								<MenuItem value="">{tcs("selectUser")}</MenuItem>
								{userOptions.map(user => (
									<MenuItem key={user.id} value={String(user.id)}>
										{user.name}
									</MenuItem>
								))}
							</TextField>
								<FormControlLabel
									control={
										<Switch
											color="primary"
											checked={Boolean(manualConfig.isDefault)}
											onChange={e =>
												handleManualFieldChange("isDefault", e.target.checked)
											}
										/>
									}
										label={tcs("makeDefaultConnection")}
								/>
							<Button
								variant="contained"
								color="primary"
								onClick={handleStartMetaSetup}
								disabled={officialSaving || metaOAuthLoading}
							>
								{metaOAuthLoading ? tcs("waitingMeta") : tcs("connectWithMeta")}
							</Button>
							<Typography variant="caption" color="textSecondary">
								{tOfficial("popupHelp")}
							</Typography>

							{editingOfficialConnection?.webhookUrl && (
								<Box>
									<Typography variant="caption" color="textSecondary">
										Webhook URL
									</Typography>
									<Typography className={classes.webhookText} variant="body2">
										{editingOfficialConnection.webhookUrl}
									</Typography>
								</Box>
							)}
						</div>
					)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					{apiModalTab !== "templates" && apiSetupMode && (
						<Button onClick={handleBackToSetupOptions} color="default">
							{tcs("back")}
						</Button>
					)}
					{apiModalTab !== "templates" && apiSetupMode === "manual" && (
						<>
							<Button
								onClick={handleSaveManualConnection}
								color="primary"
								disabled={officialSaving || officialTesting}
							>
								{tcs("save")}
							</Button>
							<Button
								onClick={handleTestManualConnection}
								color="primary"
								disabled={officialSaving || officialTesting}
							>
								{tOfficial(editingOfficialConnection?.id ? "testConnection" : "saveAndTestConnection")}
							</Button>
						</>
					)}
					<Button onClick={handleCloseApiModal} color="primary">
						{tcs("close")}
					</Button>
				</DialogActions>
			</Dialog>
			<Paper className={classes.mainPaper}>
				<MainHeader>
					<Title>{i18n.t("connections.title")}</Title>
					<MainHeaderButtonsWrapper>
						<Button
							variant="contained"
							color="primary"
							disableElevation
							className={classes.actionButton}
							onClick={handleOpenConnectionMenu}
						>
							NOVA CONEXÃO
						</Button>
						<Menu
							anchorEl={menuAnchorEl}
							keepMounted
							open={Boolean(menuAnchorEl)}
							onClose={handleCloseConnectionMenu}
						>
							<MenuItem onClick={handleOpenApiModal}>
								<ListItemIcon>
									<WhatsApp fontSize="small" />
								</ListItemIcon>
								<ListItemText primary="WhatsApp" />
							</MenuItem>
							<MenuItem onClick={handleSelectWebchatConnection}>
								<ListItemIcon>
									<Public fontSize="small" />
								</ListItemIcon>
								<ListItemText primary="Webchat" />
							</MenuItem>
							<MenuItem onClick={handleSelectFacebookConnection}>
								<ListItemIcon>
									<Facebook fontSize="small" />
								</ListItemIcon>
								<ListItemText primary="Facebook Messenger" />
							</MenuItem>
						</Menu>
					</MainHeaderButtonsWrapper>
				</MainHeader>
				<div className={classes.tableWrapper}>
				<Table size="small" className={classes.tableMinWidth}>
					<TableHead>
						<TableRow>
							<TableCell className={`${classes.colId} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.id")}
							</TableCell>
							<TableCell className={`${classes.colName} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell className={`${classes.colType} ${classes.headerTextCell}`}>Tipo</TableCell>
							<TableCell className={`${classes.colStatus} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell className={`${classes.colUpdated} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell className={`${classes.colDefault} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell className={`${classes.colActions} ${classes.headerTextCell}`}>
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton columns={7} />
						) : (
							<>
								{visibleQrConnections.length > 0 &&
									visibleQrConnections.map(whatsApp => (
										<TableRow key={`qr-${whatsApp.id}`}>
											<TableCell className={`${classes.colId} ${classes.priorityCell}`}>
												#{whatsApp.id}
											</TableCell>
											<TableCell className={`${classes.colName} ${classes.bodyTextCell}`}>
												<span className={classes.nameCellContent}>
													{renderConnectionTypeIcon(getConnectionType(whatsApp))}
													<span>{whatsApp.name}</span>
												</span>
											</TableCell>
											<TableCell className={`${classes.colType} ${classes.bodyTextCell}`}>
													{renderConnectionTypeBadge(getConnectionType(whatsApp))}
											</TableCell>
											<TableCell className={`${classes.colStatus} ${classes.priorityCell}`}>
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell className={`${classes.colUpdated} ${classes.bodyTextCell}`}>
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell className={`${classes.colDefault} ${classes.priorityCell}`}>
														{Boolean(whatsApp.isDefault || whatsApp.default) && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell className={`${classes.colActions} ${classes.priorityCell}`}>
												{canShowSessionActions(whatsApp) && (
													<Tooltip title={i18n.t("connections.buttons.sessionActions")}>
														<IconButton
															size="small"
															className={classes.actionIconButton}
															onClick={event => handleOpenSessionMenu(event, whatsApp)}
														>
															<MoreVert />
														</IconButton>
													</Tooltip>
												)}
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => {
														if (getConnectionType(whatsApp) === "webchat") {
															handleEditWebchatConnection(whatsApp);
															return;
														}

														handleEditWhatsApp(whatsApp);
													}}
												>
													<Edit />
												</IconButton>
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => {
														handleOpenDeleteWhatsAppModal(whatsApp);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								{officialLoading ? (
									<TableRowSkeleton columns={7} />
								) : (
									officialConnections.map(connection => (
										<TableRow key={`official-${connection.id}`}>
											<TableCell className={`${classes.colId} ${classes.priorityCell}`}>
												#{connection.id}
											</TableCell>
											<TableCell className={`${classes.colName} ${classes.bodyTextCell}`}>
												<span className={classes.nameCellContent}>
													{renderConnectionTypeIcon("whatsapp")}
											<span>
												{connection.name}
												{connection.onboardingMode === "coexistence"
													? ` · ${tOfficial("coexistenceBadge")}`
													: ""}
											</span>
												</span>
											</TableCell>
											<TableCell className={`${classes.colType} ${classes.bodyTextCell}`}>
												{renderConnectionTypeBadge("whatsapp")}
											</TableCell>
											<TableCell className={`${classes.colStatus} ${classes.priorityCell}`}>{renderOfficialStatusIcon(connection)}</TableCell>
											<TableCell className={`${classes.colUpdated} ${classes.bodyTextCell}`}>
												{connection.updatedAt
													? format(parseISO(connection.updatedAt), "dd/MM/yy HH:mm")
													: "-"}
											</TableCell>
											<TableCell className={`${classes.colDefault} ${classes.priorityCell}`}>
												{Boolean(connection.isDefault || connection.default) && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell className={`${classes.colActions} ${classes.priorityCell}`}>
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => handleEditOfficialConnection(connection)}
												>
													<Edit />
												</IconButton>
												<Tooltip title={tDiagnosisLabel("diagnose")}>
													<span>
														<IconButton
															size="small"
															className={classes.actionIconButton}
															onClick={() => handleDiagnoseOfficialConnection(connection)}
														>
															<BugReport />
														</IconButton>
													</span>
												</Tooltip>
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => handleOpenDeleteOfficialConnectionModal(connection)}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								)}
								{facebookLoading ? (
									<TableRowSkeleton columns={7} />
								) : (
									facebookConnections.map(connection => (
										<TableRow key={`facebook-${connection.id}`}>
											<TableCell className={`${classes.colId} ${classes.priorityCell}`}>
												#{connection.id}
											</TableCell>
											<TableCell className={`${classes.colName} ${classes.bodyTextCell}`}>
												<span className={classes.nameCellContent}>
													{renderConnectionTypeIcon("facebook")}
													<span>{connection.name}</span>
												</span>
											</TableCell>
											<TableCell className={`${classes.colType} ${classes.bodyTextCell}`}>
												{renderConnectionTypeBadge("facebook")}
											</TableCell>
											<TableCell className={`${classes.colStatus} ${classes.priorityCell}`}>{renderOfficialStatusIcon(connection)}</TableCell>
											<TableCell className={`${classes.colUpdated} ${classes.bodyTextCell}`}>
												{connection.updatedAt
													? format(parseISO(connection.updatedAt), "dd/MM/yy HH:mm")
													: "-"}
											</TableCell>
											<TableCell className={`${classes.colDefault} ${classes.priorityCell}`}>
												{Boolean(connection.isDefault || connection.default) && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell className={`${classes.colActions} ${classes.priorityCell}`}>
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => handleEditFacebookConnection(connection)}
												>
													<Edit />
												</IconButton>
												<Tooltip title={tDiagnosisLabel("diagnose")}>
													<span>
														<IconButton
															size="small"
															className={classes.actionIconButton}
															onClick={() => handleDiagnoseFacebookConnection(connection)}
														>
															<BugReport />
														</IconButton>
													</span>
												</Tooltip>
												<Tooltip title={tFacebook("subscribeWebhook")}>
													<span>
														<IconButton
															size="small"
															className={classes.actionIconButton}
															onClick={() => handleSubscribeFacebookWebhook(connection)}
															disabled={Number(facebookSubscribingId) === Number(connection.id)}
														>
															{Number(facebookSubscribingId) === Number(connection.id) ? (
																<CircularProgress size={18} />
															) : (
																<Public />
															)}
														</IconButton>
													</span>
												</Tooltip>
												<IconButton
													size="small"
													className={classes.actionIconButton}
													onClick={() => handleOpenDeleteFacebookConnectionModal(connection)}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								)}
							</>
						)}
					</TableBody>
				</Table>
				</div>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
